import {
  utcIsoToSessionTimecodeMs,
  sessionTimecodeMsToUtcIso,
  footageStartConfirmed,
} from '../../lib/sessionFootageTiming';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import RouteIcon from '@mui/icons-material/Route';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import AirOutlinedIcon from '@mui/icons-material/AirOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import useSWR from 'swr';
import {
  mapPlaybackDockLegendSx,
  mapPlaybackDockOuterSx,
  mapPlaybackDockReplaySx,
  mapPlaybackDockShellSx,
  getTraceChrome,
  traceDrawerHeaderSx,
  traceDrawerShellSx,
} from '@kangaroo/ui';

import { GpsSpeedTrackMap } from '../telemetry/GpsSpeedTrackMap';
import { AuthMedia } from '../AuthMedia';
import { TrackMapLegend, type TrackMapLegendItem } from '../telemetry/TrackMapLegend';
import { GoProSyncedVideoOverlay } from '../gopro/GoProSyncedVideoOverlay';
import { TelemetryReplayBar } from '../telemetry/TelemetryReplayBar';
import {
  SessionCompareReplaySpeedHud,
  type SessionCompareReplaySpeedReadout,
} from './SessionCompareReplaySpeedHud';
import { useSmoothedNumber } from '../../lib/useSmoothedNumber';
import { useLivePlayhead } from '../../lib/useLivePlayhead';
import {
  SessionCompareTracePanel,
  buildTraceAlignmentMappers,
  type TraceAlignmentMapper,
  SessionTraceTimeline,
  type TraceAlignmentMode,
  type TraceTimeRange,
  type TraceSessionLapWindow,
} from './SessionCompareTracePanel';
import { useMeasurementPreferences } from '../../hooks/useMeasurementPreferences';
import { getCachedCircuitLapReslice } from '../../lib/sessionGeometryCache';
import type { ResliceLapsResult } from '../../lib/circuitLapReslice';
import { sessionMapDebug } from '../../lib/sessionMapDebug';
import { buildLapTelemetryBrief } from '../../lib/lapTelemetryBrief';
import { convertSpeedKph, formatMeasurement, speedUnitLabel } from '../../lib/measurements';
import {
  buildCircuitSessionLapOptions,
  buildSessionLapOptions,
  fetchSessionExport,
  pickDefaultLapNumber,
  pickRankedLapBySpeedIndex,
} from '../../features/sessions/sessionContent';
import {
  buildCompareTrackSpeedRange,
  buildSessionCompareReplayLayers,
  buildSessionCompareTrackGroups,
  buildSessionLapAnalysis,
  buildSessionLapReplayTimeline,
  type SessionCompareReplayLayer,
} from '../../features/sessions/sessionCompareMap';
import type { CompareSlotState } from '../../features/sessions/compareSlots';
import type { SessionListItem } from '../../features/sessions/sessionList';
import type { useSessionLapSlot } from '../../features/sessions/useSessionLapSlot';
import type { LapTimingMode } from '../../features/sessions/useSessionLapSlot';
import {
  interpolateMapPointAtTime,
  buildMapPointsForGpsWindow,
  gpsPointsInWindow,
  lapWindow,
  resolveRawLoggerLaps,
  traceValuesAtTime,
  type MapReplayCursor,
  type MyChronSessionExport,
  type TrackMapMarker,
  type TrackMapPoint,
} from '../../lib/telemetrySession';
import {
  buildIdealLineMapPoints,
  fetchIdealLineReference,
  idealLineReferenceKey,
  idealLineReferenceStatusText,
} from '../../features/sessions/idealLineReference';
import { useTelemetryReplay } from '../../lib/useTelemetryReplay';
import {
  listSessionFootageUploads,
  updateGoProUploadSessionMetadata,
} from '../../lib/goproResumableUpload';
import {
  fetchTrackContributionById,
  trackContributionDetailKey,
} from '../../lib/trackContributions';
import {
  fetchTelemetrySessionContext,
  updateTelemetrySessionContext,
} from '../../features/telemetryContext';
import { createKartSetupHistoryEntry, updateKart, updateKartSetupHistoryEntry } from '../../features/karts';
import {
  SessionComparisonRail,
  type ComparisonLapRailItem,
} from './SessionComparisonRail';
import type {
  IdealLineReference,
  KartCustomAttribute,
  KartListItem,
  KartSetupSummary,
  TelemetrySessionContext,
  UpdateKartRequest,
} from '@kangaroo/shared';

type SessionsCompareMapProps = {
  viewKey?: string | null;
  idealLineAuthScope?: string | null;
  publicDemo?: boolean;
  readOnly?: boolean;
  primarySessionId: string | null;
  primarySessionExport: MyChronSessionExport | null | undefined;
  primarySessionLoading?: boolean;
  primaryLapNumber: number | null;
  lapTimingMode: LapTimingMode;
  primarySlot: ReturnType<typeof useSessionLapSlot>;
  compareSlots: CompareSlotState[];
  sessions: SessionListItem[];
  karts?: KartListItem[];
  onKartUpdated?: (kart: KartListItem) => void;
  onCompareSlotLapChange?: (slotId: string, lapNumber: number) => void;
  onRemoveCompareSlot?: (slotId: string) => void;
  onPrimaryLapChange?: (lapNumber: number) => void;
  onOpenSessionSelector?: () => void;
  loadSessionExport?: typeof fetchSessionExport;
  loadIdealLineReference?: (sessionId: string) => Promise<IdealLineReference>;
  onOpenLapMeta?: () => void;
  lapMetaDisabled?: boolean;
  onOpenFootage?: () => void;
  footageStartPickActive?: boolean;
  onFootageStartPointPick?: (point: TrackMapPoint) => void;
  reviewView?: 'map' | 'traces';
};

const TURN_SPEED_DRAWER_WIDTH = 380;
const SESSION_MAP_DISPLAY_STORAGE_KEY = 'paddiq.session-map-display.v1';
const TRACE_MAP_PANE_WIDTH_STORAGE_KEY = 'paddiq.trace-map-pane-width.v1';
const TRACE_ALIGNMENT_STORAGE_KEY = 'paddiq.trace-alignment.v1';
const DEFAULT_TRACE_MAP_PANE_WIDTH = 440;
const MIN_TRACE_MAP_PANE_WIDTH = 280;
const MIN_TRACE_CHART_PANE_WIDTH = 420;

function buildTraceLapWindows(
  sessionExport: MyChronSessionExport | null | undefined,
  lapTimingMode: LapTimingMode,
  circuitReslice: ResliceLapsResult | null | undefined,
): TraceSessionLapWindow[] {
  if (!sessionExport) return [];

  if (lapTimingMode === 'circuit' && circuitReslice?.laps.length) {
    return circuitReslice.laps.map((lap) => ({
      lapNumber: lap.lapNumber,
      startMs: lap.startMs,
      endMs: lap.endMs,
      label:
        lap.kind === 'out'
          ? 'Out'
          : lap.kind === 'in'
            ? 'In'
            : lap.kind === 'crash'
              ? 'Crash'
              : `L${lap.lapNumber}`,
      selectable: lap.kind === 'flying',
    }));
  }

  return resolveRawLoggerLaps(sessionExport).map((lap) => {
    const window = lapWindow(lap);
    return {
      lapNumber: lap.lapNumber,
      startMs: window.startMs,
      endMs: window.endMs,
      label: `L${lap.lapNumber}`,
      selectable: true,
    };
  });
}

function readTraceMapPaneWidth() {
  if (typeof window === 'undefined') return DEFAULT_TRACE_MAP_PANE_WIDTH;
  const stored = Number(window.localStorage.getItem(TRACE_MAP_PANE_WIDTH_STORAGE_KEY));
  return Number.isFinite(stored) && stored >= MIN_TRACE_MAP_PANE_WIDTH
    ? stored
    : DEFAULT_TRACE_MAP_PANE_WIDTH;
}

function readTraceAlignmentMode(): TraceAlignmentMode {
  if (typeof window === 'undefined') return 'time';
  return window.localStorage.getItem(TRACE_ALIGNMENT_STORAGE_KEY) === 'gps' ? 'gps' : 'time';
}

type SessionMapDisplayPreferences = {
  basemap: boolean;
  telemetryCards: boolean;
  replayDock: boolean;
  trackAnnotations: boolean;
};

const DEFAULT_SESSION_MAP_DISPLAY: SessionMapDisplayPreferences = {
  basemap: true,
  telemetryCards: true,
  replayDock: true,
  trackAnnotations: true,
};

function readSessionMapDisplayPreferences(): SessionMapDisplayPreferences {
  if (typeof window === 'undefined') return DEFAULT_SESSION_MAP_DISPLAY;

  try {
    const stored = JSON.parse(
      window.localStorage.getItem(SESSION_MAP_DISPLAY_STORAGE_KEY) ?? '{}',
    ) as Partial<SessionMapDisplayPreferences> & { version?: number };
    const migrateLegacyLineView = stored.version !== 2 && stored.basemap === false;
    return {
      basemap: typeof stored.basemap === 'boolean' ? stored.basemap : true,
      telemetryCards:
        typeof stored.telemetryCards === 'boolean' ? stored.telemetryCards : true,
      replayDock: migrateLegacyLineView
        ? true
        : typeof stored.replayDock === 'boolean'
          ? stored.replayDock
          : true,
      trackAnnotations:
        typeof stored.trackAnnotations === 'boolean' ? stored.trackAnnotations : true,
    };
  } catch {
    return DEFAULT_SESSION_MAP_DISPLAY;
  }
}

type EditableSetupKey =
  | 'gearRatioDriver'
  | 'gearRatioAxle'
  | 'frontTirePressurePsi'
  | 'rearTirePressurePsi'
  | 'frontTrackWidthMm'
  | 'rearTrackWidthMm'
  | 'seatPosition'
  | 'totalWeightKg';

function SetupValueInput({
  label,
  value,
  text = false,
  onCommit,
}: {
  label: string;
  value: number | string | null | undefined;
  text?: boolean;
  onCommit: (value: number | string | null) => Promise<void>;
}) {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => setDraft(value == null ? '' : String(value)), [value]);

  const commit = async () => {
    const nextValue = text ? draft.trim() || null : draft.trim() ? Number(draft) : null;
    if (!text && nextValue !== null && !Number.isFinite(nextValue)) {
      setFailed(true);
      return;
    }
    if (String(nextValue ?? '') === String(value ?? '')) return;
    setSaving(true);
    setFailed(false);
    try {
      await onCommit(nextValue);
    } catch {
      setFailed(true);
      setDraft(value == null ? '' : String(value));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TextField
      hiddenLabel
      aria-label={label}
      size="small"
      type={text ? 'text' : 'number'}
      value={draft}
      error={failed}
      disabled={saving}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.querySelector('input')?.blur();
        if (event.key === 'Escape') {
          setDraft(value == null ? '' : String(value));
          event.currentTarget.querySelector('input')?.blur();
        }
      }}
      inputProps={{ step: text ? undefined : 'any' }}
      sx={{
        width: text ? 260 : 100,
        maxWidth: '100%',
        '& .MuiInputBase-root': { height: 28, fontSize: '0.7rem', bgcolor: 'background.default' },
        '& .MuiInputBase-input': { px: 0.75, py: 0.35 },
      }}
    />
  );
}

function KartSetupComparisonDrawer({
  open,
  onClose,
  layers,
  sessions,
  karts,
  contexts,
  loading,
  onSetupChange,
  onSetupAssignmentChange,
  onCustomAttributesChange,
  onKartChange,
}: {
  open: boolean;
  onClose: () => void;
  layers: SessionCompareReplayLayer[];
  sessions: SessionListItem[];
  karts: KartListItem[];
  contexts: Record<string, TelemetrySessionContext>;
  loading: boolean;
  onSetupChange?: (options: {
    layer: SessionCompareReplayLayer;
    kart: KartListItem;
    setup: KartSetupSummary;
    setupHistoryEntryId: string | null;
    patch: Partial<KartSetupSummary>;
  }) => Promise<void>;
  onSetupAssignmentChange?: (options: {
    layer: SessionCompareReplayLayer;
    kart: KartListItem;
    setupHistoryEntryId: string | null;
  }) => Promise<void>;
  onCustomAttributesChange?: (options: {
    layer: SessionCompareReplayLayer;
    kart: KartListItem;
    setupHistoryEntryId: string | null;
    customAttributes: KartCustomAttribute[];
  }) => Promise<void>;
  onKartChange?: (kart: KartListItem, patch: UpdateKartRequest) => Promise<void>;
}) {
  const [expandedSetupIds, setExpandedSetupIds] = useState<Set<string>>(new Set());
  const [expandedCustomIds, setExpandedCustomIds] = useState<Set<string>>(new Set());
  const [expandedKartIds, setExpandedKartIds] = useState<Set<string>>(new Set());
  const columns = layers.map((layer, index) => {
    const session = sessions.find((entry) => entry.id === layer.sessionId) ?? null;
    const context = layer.sessionId ? contexts[layer.sessionId] : null;
    const lapAssignment = context?.lapSetupAssignments?.find(
      (entry) => entry.lapTimingMode === layer.lapTimingMode && entry.lapNumber === layer.lapNumber,
    );
    const kartId = lapAssignment?.kartId ?? context?.kartId ?? session?.kartId ?? null;
    const kart = karts.find((entry) => entry.id === kartId) ?? null;
    const setupEntry = kart?.setupHistory.find(
      (entry) => entry.id === (lapAssignment?.kartSetupHistoryEntryId ?? context?.kartSetupHistoryEntryId),
    );
    return {
      layer,
      index,
      session,
      kart,
      setup: setupEntry?.setup ?? kart?.setup ?? null,
      setupEntry: setupEntry ?? null,
      customAttributes: setupEntry?.customAttributes ?? kart?.customAttributes ?? [],
      lapAssignment,
      setupSource: lapAssignment ? 'Lap-specific setup' : setupEntry ? 'Inherited session setup' : kart ? 'Current setup' : 'No kart assigned',
    };
  });
  const setupFields: Array<{ label: string; field: EditableSetupKey; unit?: string; text?: boolean }> = [
    { label: 'Driver gear', field: 'gearRatioDriver' },
    { label: 'Axle gear', field: 'gearRatioAxle' },
    { label: 'Front tire pressure', field: 'frontTirePressurePsi', unit: 'psi' },
    { label: 'Rear tire pressure', field: 'rearTirePressurePsi', unit: 'psi' },
    { label: 'Front track width', field: 'frontTrackWidthMm', unit: 'mm' },
    { label: 'Rear track width', field: 'rearTrackWidthMm', unit: 'mm' },
    { label: 'Seat position', field: 'seatPosition', text: true },
    { label: 'Total weight', field: 'totalWeightKg', unit: 'kg' },
  ];
  const setupFieldGroups: Array<{
    label: string;
    fields: Array<{ label: string; field: EditableSetupKey; unit?: string; text?: boolean }>;
  }> = [
    { label: 'Gearing', fields: [{ ...setupFields[0]!, label: 'Driver' }, { ...setupFields[1]!, label: 'Axle' }] },
    { label: 'Tire pressure', fields: [{ ...setupFields[2]!, label: 'Front' }, { ...setupFields[3]!, label: 'Rear' }] },
    { label: 'Track width', fields: [{ ...setupFields[4]!, label: 'Front' }, { ...setupFields[5]!, label: 'Rear' }] },
    { label: 'Balance', fields: [{ ...setupFields[6]!, label: 'Seat' }, { ...setupFields[7]!, label: 'Weight' }] },
  ];
  const vehicleFields: Array<{
    label: string;
    field: keyof UpdateKartRequest;
    value: (kart: KartListItem) => string | number | null;
    text?: boolean;
  }> = [
    { label: 'Kart number', field: 'kartNumber', value: (kart) => kart.kartNumber, text: true },
    { label: 'Nickname', field: 'nickname', value: (kart) => kart.nickname, text: true },
    { label: 'Class', field: 'kartClass', value: (kart) => kart.kartClass, text: true },
    { label: 'Tag', field: 'tag', value: (kart) => kart.tag, text: true },
    { label: 'Chassis brand', field: 'chassisBrand', value: (kart) => kart.chassisBrand, text: true },
    { label: 'Chassis model', field: 'chassisModel', value: (kart) => kart.chassisModel, text: true },
    { label: 'Chassis year', field: 'chassisYear', value: (kart) => kart.chassisYear },
    { label: 'Chassis serial', field: 'chassisSerialNumber', value: (kart) => kart.chassisSerialNumber, text: true },
    { label: 'Engine brand', field: 'engineBrand', value: (kart) => kart.engineBrand, text: true },
    { label: 'Engine model', field: 'engineModel', value: (kart) => kart.engineModel, text: true },
    { label: 'Displacement', field: 'engineDisplacement', value: (kart) => kart.engineDisplacement },
    { label: 'Engine serial', field: 'engineSerialNumber', value: (kart) => kart.engineSerialNumber, text: true },
    { label: 'Engine hours', field: 'engineHours', value: (kart) => kart.engineHours },
    { label: 'Odometer', field: 'odometerKm', value: (kart) => kart.odometerKm },
    { label: 'Notes', field: 'notes', value: (kart) => kart.notes, text: true },
  ];
  const vehicleFieldGroups = [
    { label: 'Identity', fields: vehicleFields.slice(0, 4) },
    { label: 'Chassis', fields: vehicleFields.slice(4, 8) },
    { label: 'Engine', fields: vehicleFields.slice(8, 13) },
    { label: 'Usage', fields: vehicleFields.slice(13) },
  ];
  const drawerKarts = Array.from(
    new Map(columns.flatMap((column) => column.kart ? [[column.kart.id, column.kart] as const] : [])).values(),
  );
  const differingSetupFields = new Set(
    setupFields
      .filter(({ field }) => new Set(columns.map((column) => column.setup?.[field] ?? null)).size > 1)
      .map(({ field }) => field),
  );

  useEffect(() => {
    if (open && layers[0]) {
      setExpandedSetupIds((current) => current.size > 0 ? current : new Set([layers[0]!.id]));
    }
  }, [layers, open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 'min(92vw, 680px)' },
            bgcolor: 'background.paper',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TuneOutlinedIcon sx={{ fontSize: 19, color: 'text.secondary' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 900 }}>Kart setup comparison</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            Each lap inherits its session setup until you choose or edit a lap-specific setup.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close setup comparison"
          onClick={onClose}
          size="small"
          sx={{ ml: 'auto' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {loading ? <LinearProgress /> : null}
        <Stack spacing={1.25} sx={{ p: 1.5 }}>
          {columns.map(({ layer, index, session, kart, setup, customAttributes, setupSource, lapAssignment }) => (
            <Box key={layer.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
              <Stack
                direction="row"
                spacing={1}
                role="button"
                tabIndex={0}
                aria-expanded={expandedSetupIds.has(layer.id)}
                onClick={() => setExpandedSetupIds((current) => {
                  const next = new Set(current);
                  if (next.has(layer.id)) next.delete(layer.id);
                  else next.add(layer.id);
                  return next;
                })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') event.currentTarget.click();
                }}
                sx={{ alignItems: 'center', px: 1.5, py: 1.15, bgcolor: 'action.hover', borderBottom: expandedSetupIds.has(layer.id) ? '1px solid' : 0, borderColor: 'divider', cursor: 'pointer' }}
              >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor: layer.color,
                        color: '#08111D',
                        fontSize: '0.65rem',
                        fontWeight: 950,
                      }}
                    >
                      {index + 1}
                    </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 900 }}>{layer.label} setup</Typography>
                  <Typography noWrap sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>
                    {session?.driverName ?? 'Unassigned'} · {kart ? `#${kart.kartNumber}` : 'No kart'} · {setupSource}
                  </Typography>
                </Box>
                <ExpandMoreIcon sx={{ fontSize: 19, color: 'text.secondary', transform: expandedSetupIds.has(layer.id) ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 160ms ease' }} />
              </Stack>
              <Collapse in={expandedSetupIds.has(layer.id)} timeout={160} unmountOnExit>
                {!kart || !setup ? (
                  <Typography sx={{ p: 1.5, fontSize: '0.75rem', color: 'text.secondary' }}>Assign a kart to edit this lap’s setup.</Typography>
                ) : (
                  <Stack spacing={1.25} sx={{ p: 1.5 }}>
                  {onSetupAssignmentChange ? (
                    <Box>
                      <Typography sx={{ mb: 0.55, fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary' }}>
                        Setup used for this lap
                      </Typography>
                      <Select
                        size="small"
                        fullWidth
                        value={lapAssignment?.kartSetupHistoryEntryId ?? ''}
                        displayEmpty
                        onChange={(event) => void onSetupAssignmentChange({
                          layer,
                          kart,
                          setupHistoryEntryId: event.target.value || null,
                        })}
                        sx={{ fontSize: '0.72rem', '& .MuiSelect-select': { py: 0.75 } }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.72rem' }}>Use the session setup</MenuItem>
                        {kart.setupHistory.map((entry, historyIndex) => (
                          <MenuItem key={entry.id} value={entry.id} sx={{ fontSize: '0.72rem' }}>
                            Snapshot {kart.setupHistory.length - historyIndex} · {new Date(entry.createdAt).toLocaleDateString()}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  ) : null}

                  <Box>
                    <Typography sx={{ mb: 0.75, fontSize: '0.68rem', fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Lap setup</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                      {setupFieldGroups.map((group) => (
                        <Box key={group.label} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.25, bgcolor: 'background.default' }}>
                          <Typography sx={{ mb: 0.7, fontSize: '0.68rem', fontWeight: 850 }}>{group.label}</Typography>
                          <Stack spacing={0.65}>
                            {group.fields.map(({ label, field, unit, text }) => (
                              <Box key={field} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 100px 26px', alignItems: 'center', gap: 0.5, minHeight: 30, px: 0.5, borderRadius: 0.75, bgcolor: differingSetupFields.has(field) ? 'rgba(245, 158, 11, 0.08)' : 'transparent' }}>
                                <Typography sx={{ minWidth: 0, fontSize: '0.67rem', fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
                                <SetupValueInput
                                  label={`${layer.label} ${label}`}
                                  value={setup[field]}
                                  text={text}
                                  onCommit={async (value) => {
                                    if (onSetupChange) await onSetupChange({ layer, kart, setup, setupHistoryEntryId: lapAssignment?.kartSetupHistoryEntryId ?? null, patch: { [field]: value } });
                                  }}
                                />
                                <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>{unit ?? ''}</Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.5 }}>
                    <Button
                      fullWidth
                      color="inherit"
                      endIcon={<ExpandMoreIcon sx={{ transform: expandedCustomIds.has(layer.id) ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />}
                      onClick={() => setExpandedCustomIds((current) => {
                        const next = new Set(current);
                        if (next.has(layer.id)) next.delete(layer.id);
                        else next.add(layer.id);
                        return next;
                      })}
                      sx={{ justifyContent: 'space-between', px: 0.5, textTransform: 'none', fontSize: '0.7rem', fontWeight: 850 }}
                    >
                      Custom fields{customAttributes.length ? ` (${customAttributes.length})` : ''}
                    </Button>
                    <Collapse in={expandedCustomIds.has(layer.id)} timeout={160}>
                    <Stack spacing={0.65} sx={{ pt: 0.75 }}>
                      {customAttributes.map((attribute) => (
                        <Stack key={attribute.id} direction="row" spacing={0.65} sx={{ alignItems: 'center' }}>
                          <TextField
                            size="small"
                            defaultValue={attribute.name}
                            aria-label="Attribute name"
                            onBlur={(event) => void onCustomAttributesChange?.({ layer, kart, setupHistoryEntryId: lapAssignment?.kartSetupHistoryEntryId ?? null, customAttributes: customAttributes.map((entry) => entry.id === attribute.id ? { ...entry, name: event.target.value } : entry) })}
                            sx={{ width: 180 }}
                          />
                          <Select
                            size="small"
                            value={attribute.type}
                            aria-label={`${attribute.name} type`}
                            onChange={(event) => void onCustomAttributesChange?.({ layer, kart, setupHistoryEntryId: lapAssignment?.kartSetupHistoryEntryId ?? null, customAttributes: customAttributes.map((entry) => entry.id === attribute.id ? { ...entry, type: event.target.value as KartCustomAttribute['type'], value: null } : entry) })}
                            sx={{ width: 105, fontSize: '0.7rem' }}
                          >
                            <MenuItem value="string">Text</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="boolean">Yes / no</MenuItem>
                          </Select>
                          {attribute.type === 'boolean' ? (
                            <Switch
                              checked={attribute.value === true}
                              onChange={(_, checked) => void onCustomAttributesChange?.({ layer, kart, setupHistoryEntryId: lapAssignment?.kartSetupHistoryEntryId ?? null, customAttributes: customAttributes.map((entry) => entry.id === attribute.id ? { ...entry, value: checked } : entry) })}
                              size="small"
                            />
                          ) : (
                            <TextField
                              size="small"
                              type={attribute.type === 'number' ? 'number' : 'text'}
                              defaultValue={attribute.value ?? ''}
                              aria-label={`${attribute.name} value`}
                              onBlur={(event) => void onCustomAttributesChange?.({ layer, kart, setupHistoryEntryId: lapAssignment?.kartSetupHistoryEntryId ?? null, customAttributes: customAttributes.map((entry) => entry.id === attribute.id ? { ...entry, value: attribute.type === 'number' ? (event.target.value === '' ? null : Number(event.target.value)) : event.target.value } : entry) })}
                              sx={{ flex: 1 }}
                            />
                          )}
                          <IconButton
                            size="small"
                            aria-label={`Remove ${attribute.name}`}
                            onClick={() => void onCustomAttributesChange?.({ layer, kart, setupHistoryEntryId: lapAssignment?.kartSetupHistoryEntryId ?? null, customAttributes: customAttributes.filter((entry) => entry.id !== attribute.id) })}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Stack>
                      ))}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => void onCustomAttributesChange?.({
                          layer,
                          kart,
                          setupHistoryEntryId: lapAssignment?.kartSetupHistoryEntryId ?? null,
                          customAttributes: [...customAttributes, { id: `setup-${Date.now()}`, name: 'New attribute', type: 'string', value: null }],
                        })}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                      >
                        Add attribute
                      </Button>
                    </Stack>
                    </Collapse>
                  </Box>
                  </Stack>
                )}
              </Collapse>
            </Box>
          ))}

          {drawerKarts.length ? (
            <Box sx={{ pt: 0.5 }}>
              <Typography sx={{ mb: 0.75, px: 0.25, fontSize: '0.68rem', fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Shared kart details
              </Typography>
              <Stack spacing={1}>
                {drawerKarts.map((kart) => (
                  <Box key={kart.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                    <Button
                      fullWidth
                      color="inherit"
                      endIcon={<ExpandMoreIcon sx={{ transform: expandedKartIds.has(kart.id) ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />}
                      onClick={() => setExpandedKartIds((current) => {
                        const next = new Set(current);
                        if (next.has(kart.id)) next.delete(kart.id);
                        else next.add(kart.id);
                        return next;
                      })}
                      sx={{ justifyContent: 'space-between', px: 1.5, py: 1, textTransform: 'none', bgcolor: 'action.hover' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                        <Box sx={{ width: 44, height: 44, flexShrink: 0, overflow: 'hidden', display: 'grid', placeItems: 'center', borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                          {kart.imageUrl ? (
                            <AuthMedia
                              src={kart.imageUrl}
                              alt={`#${kart.kartNumber} ${kart.nickname || 'kart'}`}
                              sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
                            />
                          ) : (
                            <ImageOutlinedIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                          <Typography noWrap sx={{ fontSize: '0.76rem', fontWeight: 900 }}>#{kart.kartNumber} · {kart.nickname || 'Unnamed kart'}</Typography>
                          <Typography noWrap sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>{[kart.kartClass, kart.chassisBrand, kart.chassisModel].filter(Boolean).join(' · ')}</Typography>
                        </Box>
                      </Box>
                    </Button>
                    <Collapse in={expandedKartIds.has(kart.id)} timeout={160} unmountOnExit>
                      <Stack spacing={1.25} sx={{ p: 1.5 }}>
                        {vehicleFieldGroups.map((group) => (
                          <Box key={group.label}>
                            <Typography sx={{ mb: 0.65, fontSize: '0.66rem', fontWeight: 850, color: 'text.secondary' }}>{group.label}</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.75 }}>
                              {group.fields.map(({ label, field, value, text }) => (
                                <Box key={String(field)} sx={{ minWidth: 0, '& .MuiTextField-root': { width: '100%' } }}>
                                  <Typography sx={{ mb: 0.35, fontSize: '0.63rem', fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
                                  <SetupValueInput
                                    label={`${kart.kartNumber} ${label}`}
                                    value={value(kart)}
                                    text={text}
                                    onCommit={async (nextValue) => {
                                      if (onKartChange) await onKartChange(kart, { [field]: nextValue });
                                    }}
                                  />
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </Box>
    </Drawer>
  );
}

function driverInitials(name: string | null | undefined) {
  const normalized = name?.trim();
  if (!normalized || normalized.toLowerCase() === 'driver unassigned') {
    return '??';
  }

  const parts = normalized.split(/\s+/).filter(Boolean);
  return parts.length === 1
    ? parts[0]!.slice(0, 2).toUpperCase()
    : `${parts[0]![0]}${parts.at(-1)![0]}`.toUpperCase();
}

function lapSetupEditKey(layer: SessionCompareReplayLayer) {
  return `${layer.sessionId ?? 'local'}:${layer.lapTimingMode}:${layer.lapNumber}`;
}

type SessionsCompareMapSpeedHudProps = {
  alignmentMappers: Map<string, TraceAlignmentMapper>;
  replay: ReturnType<typeof useTelemetryReplay>;
  replayLayers: SessionCompareReplayLayer[];
  layerIdentityById: Map<
    string,
    {
      avatarUrl: string | null;
      initials: string | null;
      variant: number;
    }
  >;
  actionsSlot?: ReactNode;
};

/** Isolated replay HUD so speed/steering update at display refresh without re-rendering the map. */
function SessionsCompareMapSpeedHud({
  alignmentMappers,
  replay,
  replayLayers,
  layerIdentityById,
  actionsSlot,
}: SessionsCompareMapSpeedHudProps) {
  const livePlayhead = useLivePlayhead(replay);
  const isPlaying = replay.isPlaying;
  const seriesHintsRef = useRef(
    new Map<
      string,
      {
        rpm: { index: number };
        steering: { index: number };
        accX: { index: number };
        accY: { index: number };
      }
    >(),
  );

  const readouts: SessionCompareReplaySpeedReadout[] = replayLayers.map((layer) => {
    const elapsedSec = alignmentMappers.get(layer.id)?.fromAlignedSec(livePlayhead.elapsedSec)
      ?? livePlayhead.elapsedSec;
    const timecodeMs = Math.min(layer.analysis.endMs, layer.analysis.startMs + elapsedSec * 1000);
    const hints = seriesHintsRef.current.get(layer.id) ?? {
      rpm: { index: 0 },
      steering: { index: 0 },
      accX: { index: 0 },
      accY: { index: 0 },
    };
    seriesHintsRef.current.set(layer.id, hints);
    const traceValues = traceValuesAtTime(
      layer.sessionExport,
      timecodeMs,
      layer.analysis.accXBaseline ?? null,
      hints,
    );
    const identity = layerIdentityById.get(layer.id);

    return {
      id: layer.id,
      label: layer.label,
      color: layer.color,
      avatarUrl: identity?.avatarUrl ?? null,
      initials: identity?.initials ?? null,
      identityVariant: identity?.variant ?? 0,
      speedKph: traceValues?.speed ?? null,
      steeringDeg: traceValues?.steering ?? null,
      lateralG: traceValues?.accY ?? null,
      longitudinalG: traceValues?.accX ?? null,
    };
  });

  const primaryTrace = readouts[0];
  const displaySpeedKph = useSmoothedNumber(primaryTrace?.speedKph ?? null, 120, isPlaying);
  const displaySteeringDeg = useSmoothedNumber(primaryTrace?.steeringDeg ?? null, 90, isPlaying);
  const displayLateralG = useSmoothedNumber(primaryTrace?.lateralG ?? null, 100, isPlaying);
  const displayLongitudinalG = useSmoothedNumber(
    primaryTrace?.longitudinalG ?? null,
    100,
    isPlaying,
  );
  const displayElapsedSec =
    useSmoothedNumber(livePlayhead.elapsedSec, 80, isPlaying) ?? livePlayhead.elapsedSec;

  if (!primaryTrace) {
    return null;
  }

  const smoothedReadouts = readouts.map((readout, index) =>
    index === 0
      ? {
          ...readout,
          speedKph: displaySpeedKph ?? readout.speedKph,
          steeringDeg: displaySteeringDeg ?? readout.steeringDeg,
          lateralG: displayLateralG ?? readout.lateralG,
          longitudinalG: displayLongitudinalG ?? readout.longitudinalG,
        }
      : readout,
  );

  return (
    <SessionCompareReplaySpeedHud
      readouts={smoothedReadouts}
      elapsedSec={displayElapsedSec}
      steeringDeg={displaySteeringDeg ?? primaryTrace.steeringDeg}
      lateralG={displayLateralG ?? primaryTrace.lateralG}
      longitudinalG={displayLongitudinalG ?? primaryTrace.longitudinalG}
      actionsSlot={actionsSlot}
    />
  );
}

function SessionMapLoadingOverlay({
  label,
  compact = false,
}: {
  label: string;
  compact?: boolean;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (compact) {
    return (
      <LinearProgress
        aria-label={label}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 20,
          height: 3,
          pointerEvents: 'none',
          bgcolor: 'rgba(148,163,184,0.12)',
          '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(90deg, #B78CFF 0%, #00D5FF 100%)',
          },
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isDark ? 'rgba(2,10,18,0.72)' : 'rgba(248,250,252,0.82)',
        backdropFilter: 'blur(6px)',
        pointerEvents: 'none',
      }}
    >
      <LinearProgress
        aria-label={label}
        sx={{
          height: 3,
          flexShrink: 0,
          bgcolor: 'rgba(148,163,184,0.12)',
          '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(90deg, #B78CFF 0%, #00D5FF 100%)',
          },
        }}
      />
      <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', px: 3 }}>
        <Stack spacing={1.1} sx={{ alignItems: 'center', maxWidth: 360 }}>
          <CircularProgress size={30} thickness={4.5} sx={{ color: isDark ? '#B78CFF' : '#64748B' }} />
          <Typography
            sx={{ fontSize: '0.84rem', color: isDark ? '#D8DEE8' : '#475569', fontWeight: 800, textAlign: 'center' }}
          >
            {label}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

type SessionCompareTracePanelLiveProps = {
  replay: ReturnType<typeof useTelemetryReplay>;
  layers: SessionCompareReplayLayer[];
  durationSec: number;
  alignmentMode: TraceAlignmentMode;
  tracePickerOpen: boolean;
  onTracePickerClose: () => void;
  onSeekSec: (elapsedSec: number) => void;
  onTraceZoomRangeChange?: (range: TraceTimeRange | null) => void;
  fillHeight?: boolean;
  saveDialogOpen?: boolean;
  onSaveDialogClose?: () => void;
};

/** Trace charts sample playhead locally so the map tree does not re-render during replay. */
function SessionCompareTracePanelLive({
  replay,
  layers,
  durationSec,
  alignmentMode,
  tracePickerOpen,
  onTracePickerClose,
  onSeekSec,
  onTraceZoomRangeChange,
  fillHeight = false,
  saveDialogOpen = false,
  onSaveDialogClose,
}: SessionCompareTracePanelLiveProps) {
  const livePlayhead = useLivePlayhead(replay);
  const cursorSec = replay.isPlaying ? livePlayhead.elapsedSec : replay.elapsedSec;
  const selectedRange = replay.loopStartSec !== null && replay.loopEndSec !== null
    ? { startSec: replay.loopStartSec, endSec: replay.loopEndSec }
    : null;

  return (
    <SessionCompareTracePanel
      drawer
      layers={layers}
      cursorSec={cursorSec}
      durationSec={durationSec}
      alignmentMode={alignmentMode}
      tracePickerOpen={tracePickerOpen}
      onTracePickerClose={onTracePickerClose}
      onSeekSec={onSeekSec}
      onTraceZoomRangeChange={onTraceZoomRangeChange}
      fillHeight={fillHeight}
      saveDialogOpen={saveDialogOpen}
      onSaveDialogClose={onSaveDialogClose}
      selectedRange={selectedRange}
      onSelectRange={(startSec, endSec) => {
        replay.pause();
        replay.setLoopStart(startSec);
        replay.setLoopEnd(endSec);
        replay.seekElapsed(startSec);
      }}
      onReplaySelection={() => {
        if (!selectedRange) return;
        if (!replay.loopEnabled) replay.toggleLoop();
        replay.seekElapsed(selectedRange.startSec);
        replay.play();
      }}
      onClearSelection={replay.clearLoop}
    />
  );
}

function SessionTraceTimelineLive({
  replay,
  layer,
  windows,
  sourceLabel,
  zoomRange,
  onSelectLap,
  chrome,
  alignmentMapper,
}: {
  replay: ReturnType<typeof useTelemetryReplay>;
  layer: SessionCompareReplayLayer;
  windows: TraceSessionLapWindow[];
  sourceLabel: string;
  alignmentMapper?: TraceAlignmentMapper;
  zoomRange: TraceTimeRange | null;
  onSelectLap?: (lapNumber: number) => void;
  chrome: ReturnType<typeof getTraceChrome>;
}) {
  const livePlayhead = useLivePlayhead(replay);
  const cursorSec = replay.isPlaying ? livePlayhead.elapsedSec : replay.elapsedSec;

  return (
    <SessionTraceTimeline
      windows={windows}
      gps={layer.sessionExport.gps}
      sourceLabel={sourceLabel}
      selectedLapNumber={layer.lapNumber}
      currentLapStartMs={layer.analysis.startMs}
      currentLapDurationSec={layer.analysis.durationSec}
      cursorSec={alignmentMapper?.fromAlignedSec(cursorSec) ?? cursorSec}
      viewStartSec={alignmentMapper?.fromAlignedSec(zoomRange?.startSec ?? 0) ?? zoomRange?.startSec ?? 0}
      viewEndSec={zoomRange ? alignmentMapper?.fromAlignedSec(zoomRange.endSec) ?? zoomRange.endSec : layer.analysis.durationSec}
      onSeekSec={(elapsedSec) => {
        replay.pause();
        replay.seekElapsed(alignmentMapper?.toAlignedSec(elapsedSec) ?? elapsedSec);
      }}
      onSelectLap={onSelectLap}
      chrome={chrome}
    />
  );
}

type SessionsCompareMapTrackProps = {
  showBasemap: boolean;
  showTrackAnnotations: boolean;
  viewKey: string;
  mapPoints: TrackMapPoint[];
  displayTrackGroups: TrackMapPoint[][];
  referenceTrackGroups: TrackMapPoint[][];
  mapMarkers: TrackMapMarker[];
  startFinishLine?: { pointA: { lat: number; lon: number }; pointB: { lat: number; lon: number } };
  compareSpeedRange: ReturnType<typeof buildCompareTrackSpeedRange>;
  idealPoints: TrackMapPoint[];
  showIdealLine: boolean;
  legendTrackItems: TrackMapLegendItem[];
  cursorTimecodeMs: number | null;
  replayCursors: MapReplayCursor[];
  resolveReplayCursorTimecodeMsRef: MutableRefObject<
    ((cursor: MapReplayCursor) => number | null) | undefined
  >;
  followReplayCursor: boolean;
  onOverlaysReady?: () => void;
  onMapLatLngClick?: (coord: { lat: number; lon: number }) => void;
  onMapMarkerClick?: (markerId: string) => void;
  brakingOverlayEnabled: boolean;
  coastingOverlayEnabled: boolean;
};

/** Isolated map layer — cursor motion runs on rAF; this tree should not re-render during playback. */
const SessionsCompareMapTrack = memo(function SessionsCompareMapTrack({
  showBasemap,
  showTrackAnnotations,
  viewKey,
  mapPoints,
  displayTrackGroups,
  referenceTrackGroups,
  mapMarkers,
  startFinishLine,
  compareSpeedRange,
  idealPoints,
  showIdealLine,
  legendTrackItems,
  cursorTimecodeMs,
  replayCursors,
  resolveReplayCursorTimecodeMsRef,
  followReplayCursor,
  onOverlaysReady,
  onMapLatLngClick,
  onMapMarkerClick,
  brakingOverlayEnabled,
  coastingOverlayEnabled,
}: SessionsCompareMapTrackProps) {
  return (
    <GpsSpeedTrackMap
      basemapProvider="mapbox"
      showBasemap={showBasemap}
      points={mapPoints}
      trackGroups={displayTrackGroups}
      referenceTrackGroups={referenceTrackGroups}
      mapMarkers={mapMarkers}
      startFinishLine={startFinishLine}
      speedRange={compareSpeedRange ?? undefined}
      showLapLines
      fill
      showLegend={false}
      showBrakingSpots={showTrackAnnotations}
      showCoastingSpots={showTrackAnnotations}
      showBrakingToggle={false}
      brakingOverlayEnabled={brakingOverlayEnabled}
      coastingOverlayEnabled={coastingOverlayEnabled}
      resetViewKey={viewKey}
      heatLineWidth={2.75}
      heatLineHaloWidth={5.5}
      heatLineWhiteHalo={false}
      mapMarkerLightOutline={false}
      mapMarkerScale={1.45}
      idealPoints={idealPoints}
      showIdealLine={showIdealLine}
      legendTrackItems={legendTrackItems}
      cursorTimecodeMs={cursorTimecodeMs}
      replayCursors={replayCursors}
      resolveReplayCursorTimecodeMsRef={resolveReplayCursorTimecodeMsRef}
      followReplayCursor={followReplayCursor}
      onOverlaysReady={onOverlaysReady}
      onMapLatLngClick={onMapLatLngClick}
      onMapMarkerClick={onMapMarkerClick}
    />
  );
});

export function SessionsCompareMap({
  viewKey = null,
  idealLineAuthScope = null,
  publicDemo = false,
  readOnly = false,
  primarySessionId,
  primarySessionExport,
  primarySessionLoading = false,
  primaryLapNumber,
  lapTimingMode,
  primarySlot,
  compareSlots,
  sessions,
  karts = [],
  onKartUpdated,
  onCompareSlotLapChange,
  onRemoveCompareSlot,
  onPrimaryLapChange,
  onOpenSessionSelector,
  loadSessionExport = fetchSessionExport,
  loadIdealLineReference,
  onOpenLapMeta,
  lapMetaDisabled = false,
  onOpenFootage,
  footageStartPickActive = false,
  onFootageStartPointPick,
  reviewView: controlledReviewView,
}: SessionsCompareMapProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const traceChrome = getTraceChrome(isDark);
  const measurementPreferences = useMeasurementPreferences();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const traceLayoutRef = useRef<HTMLDivElement | null>(null);
  const mapPaneResizeStartRef = useRef<{
    pointerId: number;
    clientX: number;
    width: number;
  } | null>(null);
  const [mapPaneWidth, setMapPaneWidth] = useState(readTraceMapPaneWidth);
  const [mapPaneResizing, setMapPaneResizing] = useState(false);
  const [traceDrawerOpen, setTraceDrawerOpen] = useState(false);
  const [traceSaveDialogOpen, setTraceSaveDialogOpen] = useState(false);
  const [kartSetupsOpen, setKartSetupsOpen] = useState(false);
  const setupEditEntryIdsRef = useRef<Record<string, string>>({});
  const setupEditDraftsRef = useRef<Record<string, KartSetupSummary>>({});
  const setupEditQueuesRef = useRef<Record<string, Promise<void>>>({});
  const reviewView = controlledReviewView ?? 'map';
  const [overlayMenuAnchor, setOverlayMenuAnchor] = useState<HTMLElement | null>(null);
  const [mapDisplayMenuAnchor, setMapDisplayMenuAnchor] = useState<HTMLElement | null>(null);
  const [traceActionsMenuAnchor, setTraceActionsMenuAnchor] = useState<HTMLElement | null>(null);
  const [preferredTraceAlignmentMode, setTraceAlignmentMode] = useState<TraceAlignmentMode>(
    readTraceAlignmentMode,
  );
  const [mapDisplay, setMapDisplay] = useState<SessionMapDisplayPreferences>(
    readSessionMapDisplayPreferences,
  );
  const [brakingOverlayEnabled, setBrakingOverlayEnabled] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('paddiq:map:braking-overlay') !== 'false';
  });
  const [coastingOverlayEnabled, setCoastingOverlayEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('paddiq:map:coasting-overlay') === 'true';
  });
  const [tracePickerOpen, setTracePickerOpen] = useState(false);
  const [traceZoomRange, setTraceZoomRange] = useState<TraceTimeRange | null>(null);
  const [activeTimelineSlotId, setActiveTimelineSlotId] = useState<string | null>(null);
  const [turnDrawerOpen, setTurnDrawerOpen] = useState(false);
  const [renderedCircuitOverlayKey, setRenderedCircuitOverlayKey] = useState<string | null>(null);
  const [mediaDurationsSec, setMediaDurationsSec] = useState<Record<string, number>>({});
  const resolvedContributionId = primarySlot.resolvedCircuit.contributionId;

  const updateBrakingOverlay = useCallback((enabled: boolean) => {
    setBrakingOverlayEnabled(enabled);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('paddiq:map:braking-overlay', String(enabled));
    }
  }, []);
  const updateCoastingOverlay = useCallback((enabled: boolean) => {
    setCoastingOverlayEnabled(enabled);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('paddiq:map:coasting-overlay', String(enabled));
    }
  }, []);
  const updateMapDisplay = useCallback((updates: Partial<SessionMapDisplayPreferences>) => {
    setMapDisplay((current) => {
      const next = { ...current, ...updates };
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(
          SESSION_MAP_DISPLAY_STORAGE_KEY,
          JSON.stringify({ ...next, version: 2 }),
        );
      }
      return next;
    });
  }, []);
  const updateMapPaneWidth = useCallback((requestedWidth: number) => {
    const layoutWidth = traceLayoutRef.current?.getBoundingClientRect().width ?? 1280;
    const maximumWidth = Math.max(
      MIN_TRACE_MAP_PANE_WIDTH,
      layoutWidth - MIN_TRACE_CHART_PANE_WIDTH - 12,
    );
    const nextWidth = Math.round(
      Math.min(maximumWidth, Math.max(MIN_TRACE_MAP_PANE_WIDTH, requestedWidth)),
    );
    setMapPaneWidth(nextWidth);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(TRACE_MAP_PANE_WIDTH_STORAGE_KEY, String(nextWidth));
    }
  }, []);
  const resetMapPaneWidth = useCallback(() => {
    setMapPaneWidth(DEFAULT_TRACE_MAP_PANE_WIDTH);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(TRACE_MAP_PANE_WIDTH_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!traceDrawerOpen) {
      setTracePickerOpen(false);
    }
  }, [traceDrawerOpen]);

  useEffect(() => {
    if (reviewView === 'traces') setTraceDrawerOpen(false);
  }, [reviewView]);

  useEffect(() => {
    setActiveTimelineSlotId(null);
  }, [primarySessionId]);

  useEffect(() => {
    if (
      activeTimelineSlotId
      && !compareSlots.some((slot) => slot.id === activeTimelineSlotId)
    ) {
      setActiveTimelineSlotId(null);
    }
  }, [activeTimelineSlotId, compareSlots]);

  const compareSessionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const slot of compareSlots) {
      if (slot.sessionId && slot.sessionId !== primarySessionId) {
        ids.add(slot.sessionId);
      }
    }
    return [...ids].sort();
  }, [compareSlots, primarySessionId]);

  const { data: compareExports = {}, isLoading: compareExportsLoading } = useSWR(
    compareSessionIds.length > 0
      ? (['sessions', 'compare-map-exports', reviewView, ...compareSessionIds] as const)
      : null,
    async () => {
      const entries = await Promise.all(
        compareSessionIds.map(async (sessionId) => {
          const payload = await loadSessionExport(sessionId, {
            view: reviewView,
            requestScope: `compare-session-review:${sessionId}`,
          });
          return [sessionId, payload] as const;
        }),
      );
      return Object.fromEntries(entries);
    },
    { revalidateOnFocus: false },
  );

  const [aligningFootageId, setAligningFootageId] = useState<string | null>(null);
  const { data: sessionFootage = [], mutate: mutateSessionFootage } = useSWR(
    primarySessionId && !publicDemo
      ? (['session-footage-overlay', primarySessionId] as const)
      : null,
    ([, activeSessionId]) => listSessionFootageUploads(activeSessionId),
    { revalidateOnFocus: false },
  );

  const primarySelectedLap =
    lapTimingMode === 'circuit' ? primarySlot.selectedLapNumber : primaryLapNumber;

  const compareCircuitReslices = useMemo(() => {
    if (lapTimingMode !== 'circuit' || !primarySlot.resolvedCircuit.overlay?.startFinishLine) {
      return {};
    }

    return Object.fromEntries(
      compareSessionIds.map((sessionId) => {
        if (sessionId === primarySessionId) {
          return [sessionId, primarySlot.circuitLapReslice.activeReslice];
        }

        const session = compareExports[sessionId];
        const reslice =
          session && primarySlot.resolvedCircuit.overlay
            ? getCachedCircuitLapReslice(sessionId, primarySlot.resolvedCircuit.overlay, session)
            : null;
        return [sessionId, reslice];
      }),
    );
  }, [
    compareExports,
    compareSessionIds,
    lapTimingMode,
    primarySessionId,
    primarySlot.circuitLapReslice.activeReslice,
    primarySlot.resolvedCircuit.overlay,
  ]);

  useEffect(() => {
    if (!onCompareSlotLapChange) return;
    compareSlots.forEach((slot) => {
      if (slot.lapNumber !== null) return;
      const sessionExport = slot.sessionId === primarySessionId
        ? primarySessionExport
        : compareExports[slot.sessionId];
      if (!sessionExport) return;
      const circuitLaps = buildCircuitSessionLapOptions(compareCircuitReslices[slot.sessionId]);
      const defaultLap = lapTimingMode === 'circuit' && circuitLaps.length
        ? pickRankedLapBySpeedIndex(circuitLaps, 0)
        : pickDefaultLapNumber(sessionExport);
      if (defaultLap !== null) onCompareSlotLapChange(slot.id, defaultLap);
    });
  }, [compareCircuitReslices, compareExports, compareSlots, lapTimingMode, onCompareSlotLapChange, primarySessionExport, primarySessionId]);

  const trackGroups = useMemo(
    () =>
      buildSessionCompareTrackGroups({
        primarySessionExport,
        primarySessionId,
        primaryLapNumber,
        primarySelectedCircuitLap: primarySlot.selectedLapNumber,
        lapTimingMode,
        primaryCircuitReslice: primarySlot.circuitLapReslice.activeReslice,
        compareCircuitReslices,
        compareSlots,
        compareExports,
      }),
    [
      compareExports,
      compareCircuitReslices,
      compareSlots,
      lapTimingMode,
      primaryLapNumber,
      primarySessionExport,
      primarySessionId,
      primarySlot.circuitLapReslice.activeReslice,
      primarySlot.selectedLapNumber,
    ],
  );

  const replayLayers = useMemo(
    () =>
      buildSessionCompareReplayLayers({
        primarySessionExport,
        primarySessionId,
        primaryLapNumber,
        primarySelectedCircuitLap: primarySlot.selectedLapNumber,
        lapTimingMode,
        primaryCircuitReslice: primarySlot.circuitLapReslice.activeReslice,
        compareCircuitReslices,
        compareSlots,
        compareExports,
      }),
    [
      compareExports,
      compareCircuitReslices,
      compareSlots,
      lapTimingMode,
      primaryLapNumber,
      primarySessionExport,
      primarySessionId,
      primarySlot.circuitLapReslice.activeReslice,
      primarySlot.selectedLapNumber,
    ],
  );

  const setupSessionIds = useMemo(
    () => [...new Set(
      replayLayers
        .map((layer) => layer.sessionId)
        .filter((sessionId): sessionId is string => Boolean(sessionId)),
    )].sort(),
    [replayLayers],
  );
  const {
    data: setupContexts = {},
    isLoading: setupContextsLoading,
    mutate: mutateSetupContexts,
  } = useSWR(
    !publicDemo && replayLayers.length > 0 && setupSessionIds.length > 0
      ? (['sessions', 'kart-setup-contexts', ...setupSessionIds] as const)
      : null,
    async () => {
      const entries = await Promise.all(
        setupSessionIds.map(async (sessionId) => [
          sessionId,
          await fetchTelemetrySessionContext(sessionId),
        ] as const),
      );
      return Object.fromEntries(entries);
    },
    { revalidateOnFocus: false },
  );

  const handleSetupChange = useCallback(async ({
    layer,
    kart,
    setup,
    setupHistoryEntryId,
    patch,
  }: {
    layer: SessionCompareReplayLayer;
    kart: KartListItem;
    setup: KartSetupSummary;
    setupHistoryEntryId: string | null;
    patch: Partial<KartSetupSummary>;
  }) => {
    const editKey = lapSetupEditKey(layer);
    const nextSetup = {
      ...(setupEditDraftsRef.current[editKey] ?? setup),
      ...patch,
    };
    setupEditDraftsRef.current[editKey] = nextSetup;

    const save = async () => {
      const activeEntryId = setupEditEntryIdsRef.current[editKey] ?? setupHistoryEntryId;
      const existingEntryIds = new Set(kart.setupHistory.map((entry) => entry.id));
      const updatedKart = activeEntryId
        ? await updateKartSetupHistoryEntry(kart.id, activeEntryId, { setup: patch })
        : await createKartSetupHistoryEntry(kart.id, {
            setup: nextSetup,
            customAttributes: kart.customAttributes,
            notes: kart.notes,
          });
      const setupEntry = activeEntryId
        ? updatedKart.setupHistory.find((entry) => entry.id === activeEntryId) ?? null
        : updatedKart.setupHistory.find((entry) => !existingEntryIds.has(entry.id)) ?? null;

      if (setupEntry) setupEditEntryIdsRef.current[editKey] = setupEntry.id;

      if (layer.sessionId && setupEntry) {
        const context = setupContexts[layer.sessionId];
        const retained = (context?.lapSetupAssignments ?? []).filter(
          (entry) => entry.lapTimingMode !== layer.lapTimingMode || entry.lapNumber !== layer.lapNumber,
        );
        const updatedContext = await updateTelemetrySessionContext(layer.sessionId, {
          lapSetupAssignments: [...retained, {
            lapTimingMode: layer.lapTimingMode,
            lapNumber: layer.lapNumber,
            kartId: updatedKart.id,
            kartSetupHistoryEntryId: setupEntry.id,
          }],
        });
        await mutateSetupContexts(
          (current) => ({ ...current, [layer.sessionId!]: updatedContext }),
          false,
        );
      }

      onKartUpdated?.(updatedKart);
    };

    const queuedSave = (setupEditQueuesRef.current[editKey] ?? Promise.resolve())
      .catch(() => undefined)
      .then(save);
    setupEditQueuesRef.current[editKey] = queuedSave;
    try {
      await queuedSave;
    } finally {
      if (setupEditQueuesRef.current[editKey] === queuedSave) {
        delete setupEditQueuesRef.current[editKey];
      }
    }
  }, [mutateSetupContexts, onKartUpdated, setupContexts]);

  const handleKartChange = useCallback(async (kart: KartListItem, patch: UpdateKartRequest) => {
    const updatedKart = await updateKart(kart.id, patch);
    onKartUpdated?.(updatedKart);
  }, [onKartUpdated]);

  const handleSetupAssignmentChange = useCallback(async ({
    layer,
    kart,
    setupHistoryEntryId,
  }: {
    layer: SessionCompareReplayLayer;
    kart: KartListItem;
    setupHistoryEntryId: string | null;
  }) => {
    if (!layer.sessionId) return;
    const editKey = lapSetupEditKey(layer);
    if (setupHistoryEntryId) setupEditEntryIdsRef.current[editKey] = setupHistoryEntryId;
    else delete setupEditEntryIdsRef.current[editKey];
    delete setupEditDraftsRef.current[editKey];
    const context = setupContexts[layer.sessionId];
    const retained = (context?.lapSetupAssignments ?? []).filter(
      (entry) => entry.lapTimingMode !== layer.lapTimingMode || entry.lapNumber !== layer.lapNumber,
    );
    const lapSetupAssignments = setupHistoryEntryId
      ? [...retained, {
          lapTimingMode: layer.lapTimingMode,
          lapNumber: layer.lapNumber,
          kartId: kart.id,
          kartSetupHistoryEntryId: setupHistoryEntryId,
        }]
      : retained;
    const updatedContext = await updateTelemetrySessionContext(layer.sessionId, { lapSetupAssignments });
    await mutateSetupContexts(
      (current) => ({ ...current, [layer.sessionId!]: updatedContext }),
      false,
    );
  }, [mutateSetupContexts, setupContexts]);

  const handleCustomAttributesChange = useCallback(async ({
    layer,
    kart,
    setupHistoryEntryId,
    customAttributes,
  }: {
    layer: SessionCompareReplayLayer;
    kart: KartListItem;
    setupHistoryEntryId: string | null;
    customAttributes: KartCustomAttribute[];
  }) => {
    const editKey = lapSetupEditKey(layer);
    const save = async () => {
      const activeEntryId = setupEditEntryIdsRef.current[editKey] ?? setupHistoryEntryId;
      const existingEntryIds = new Set(kart.setupHistory.map((entry) => entry.id));
      const updatedKart = activeEntryId
        ? await updateKartSetupHistoryEntry(kart.id, activeEntryId, { customAttributes })
        : await createKartSetupHistoryEntry(kart.id, {
            setup: setupEditDraftsRef.current[editKey] ?? kart.setup,
            customAttributes,
            notes: kart.notes,
          });
      const setupEntry = activeEntryId
        ? updatedKart.setupHistory.find((entry) => entry.id === activeEntryId) ?? null
        : updatedKart.setupHistory.find((entry) => !existingEntryIds.has(entry.id)) ?? null;
      if (setupEntry) setupEditEntryIdsRef.current[editKey] = setupEntry.id;
      onKartUpdated?.(updatedKart);

      if (!activeEntryId && layer.sessionId && setupEntry) {
        await handleSetupAssignmentChange({ layer, kart: updatedKart, setupHistoryEntryId: setupEntry.id });
      }
    };

    const queuedSave = (setupEditQueuesRef.current[editKey] ?? Promise.resolve())
      .catch(() => undefined)
      .then(save);
    setupEditQueuesRef.current[editKey] = queuedSave;
    try {
      await queuedSave;
    } finally {
      if (setupEditQueuesRef.current[editKey] === queuedSave) {
        delete setupEditQueuesRef.current[editKey];
      }
    }
  }, [handleSetupAssignmentChange, onKartUpdated]);

  useEffect(() => {
    if (replayLayers.length === 0) setKartSetupsOpen(false);
  }, [replayLayers.length]);

  const primaryReplayTimeline = useMemo(() => {
    if (!primarySessionExport || primarySelectedLap == null) {
      return null;
    }

    return buildSessionLapReplayTimeline(
      primarySessionExport,
      primarySelectedLap,
      lapTimingMode,
      primarySlot.circuitLapReslice.activeReslice,
    );
  }, [
    lapTimingMode,
    primarySelectedLap,
    primarySessionExport,
    primarySlot.circuitLapReslice.activeReslice,
  ]);

  const traceAlignmentMode: TraceAlignmentMode = preferredTraceAlignmentMode === 'gps'
    && replayLayers.some((layer) => layer.id === 'primary')
    && replayLayers.every((layer) => layer.points.length >= 2)
    ? 'gps' : 'time';

  const replayTimeline = useMemo(() => {
    if (!primaryReplayTimeline) {
      return null;
    }

    if (traceAlignmentMode === 'gps') return primaryReplayTimeline;

    const durationSec = Math.max(
      primaryReplayTimeline.durationSec,
      ...replayLayers.map((layer) => layer.analysis.durationSec),
    );

    return {
      ...primaryReplayTimeline,
      durationSec,
      endTimecodeMs: primaryReplayTimeline.startTimecodeMs + durationSec * 1000,
      laps: primaryReplayTimeline.laps.map((lap) => ({
        ...lap,
        endMs: primaryReplayTimeline.startTimecodeMs + durationSec * 1000,
        durationSec,
      })),
    };
  }, [primaryReplayTimeline, replayLayers, traceAlignmentMode]);

  const replay = useTelemetryReplay(replayTimeline);
  useEffect(() => {
    replay.reset();
  }, [traceAlignmentMode, replay.reset]);
  const primaryLayer = replayLayers.find((layer) => layer.id === 'primary') ?? null;
  const handleSessionTimelineLapSelect = useCallback((lapNumber: number) => {
    if (lapNumber === primarySelectedLap) return;

    replay.pause();
    if (lapTimingMode === 'circuit') {
      primarySlot.circuitLapReslice.setSelectedLapNumber(lapNumber);
      return;
    }
    onPrimaryLapChange?.(lapNumber);
  }, [
    lapTimingMode,
    onPrimaryLapChange,
    primarySelectedLap,
    primarySlot.circuitLapReslice,
    replay,
  ]);
  const sessionTimelineLapSelect =
    lapTimingMode === 'circuit' || onPrimaryLapChange
      ? handleSessionTimelineLapSelect
      : undefined;
  const activeTimelineSlotIndex = activeTimelineSlotId
    ? compareSlots.findIndex((slot) => slot.id === activeTimelineSlotId)
    : -1;
  const activeTimelineSlot = activeTimelineSlotIndex >= 0
    ? compareSlots[activeTimelineSlotIndex] ?? null
    : null;
  const activeTimelineSessionExport = activeTimelineSlot
    ? activeTimelineSlot.sessionId === primarySessionId
      ? primarySessionExport
      : compareExports[activeTimelineSlot.sessionId]
    : primarySessionExport;
  const activeTimelineCircuitReslice = activeTimelineSlot
    ? activeTimelineSlot.sessionId === primarySessionId
      ? primarySlot.circuitLapReslice.activeReslice
      : compareCircuitReslices[activeTimelineSlot.sessionId]
    : primarySlot.circuitLapReslice.activeReslice;
  const activeTimelineLayer = activeTimelineSlot
    ? replayLayers.find((layer) => (
        layer.slotId === activeTimelineSlot.id
        && layer.sessionId === activeTimelineSlot.sessionId
        && layer.lapNumber === activeTimelineSlot.lapNumber
      )) ?? null
    : primaryLayer;
  const timelineAlignmentMappers = useMemo(
    () => buildTraceAlignmentMappers(replayLayers, traceAlignmentMode),
    [replayLayers, traceAlignmentMode],
  );
  useEffect(() => {
    if (activeTimelineSlotId && (!activeTimelineSlot || (!activeTimelineLayer && !compareExportsLoading))) setActiveTimelineSlotId(null);
  }, [activeTimelineSlotId, activeTimelineSlot, activeTimelineLayer, compareExportsLoading]);
  const activeTimelineWindows = useMemo(
    () => buildTraceLapWindows(
      activeTimelineSessionExport,
      lapTimingMode,
      activeTimelineCircuitReslice,
    ),
    [activeTimelineCircuitReslice, activeTimelineSessionExport, lapTimingMode],
  );
  const handleActiveTimelineLapSelect = useCallback((lapNumber: number) => {
    if (!activeTimelineSlot) {
      handleSessionTimelineLapSelect(lapNumber);
      return;
    }
    if (lapNumber === activeTimelineSlot.lapNumber || !onCompareSlotLapChange) return;
    replay.pause();
    onCompareSlotLapChange(activeTimelineSlot.id, lapNumber);
  }, [
    activeTimelineSlot,
    handleSessionTimelineLapSelect,
    onCompareSlotLapChange,
    replay,
  ]);
  const activeTimelineLapSelect = activeTimelineSlot
    ? onCompareSlotLapChange ? handleActiveTimelineLapSelect : undefined
    : sessionTimelineLapSelect;
  const activeTimelineLabel = activeTimelineSlot
    ? `Compare ${activeTimelineSlotIndex + 1}`
    : 'Primary';
  const comparisonLapItems = useMemo<ComparisonLapRailItem[]>(() => {
    const primarySession = sessions.find((session) => session.id === primarySessionId) ?? null;
    const primaryLoggerLaps = buildSessionLapOptions(primarySessionExport);
    const primaryLaps = primarySlot.laps.length > 0 ? primarySlot.laps : primaryLoggerLaps;
    const colors = ['#FFB000', '#00D5FF', '#2EE66B', '#FF5C8A'];
    const items: ComparisonLapRailItem[] = [
      {
        id: `primary:${primarySessionId}`,
        roleLabel: 'Primary',
        color: '#B78CFF',
        session: primarySession,
        selectedLapNumber: primarySelectedLap,
        laps: primaryLaps,
        active: activeTimelineSlotId === null,
        loading: primarySessionLoading,
        onLapChange: sessionTimelineLapSelect,
        onActivate: () => {
          replay.pause();
          setActiveTimelineSlotId(null);
        },
      },
    ];

    compareSlots.forEach((slot, index) => {
      const session = sessions.find((entry) => entry.id === slot.sessionId) ?? null;
      const sessionExport = slot.sessionId === primarySessionId
        ? primarySessionExport
        : compareExports[slot.sessionId];
      const requestedLaps = slot.sessionId === primarySessionId
        ? primaryLaps
        : lapTimingMode === 'circuit'
          ? buildCircuitSessionLapOptions(compareCircuitReslices[slot.sessionId])
          : buildSessionLapOptions(sessionExport);
      const laps = requestedLaps.length > 0 ? requestedLaps : buildSessionLapOptions(sessionExport);

      items.push({
        id: slot.id,
        roleLabel: `Compare ${index + 1}`,
        color: colors[index % colors.length]!,
        session,
        selectedLapNumber: slot.lapNumber,
        laps,
        active: activeTimelineSlotId === slot.id,
        loading: Boolean(slot.sessionId && !sessionExport && compareExportsLoading),
        onLapChange: onCompareSlotLapChange
          ? (lapNumber) => onCompareSlotLapChange(slot.id, lapNumber)
          : undefined,
        onActivate: replayLayers.some((layer) => layer.slotId === slot.id)
          ? () => {
              replay.pause();
              setActiveTimelineSlotId(slot.id);
            }
          : undefined,
        onRemove: onRemoveCompareSlot ? () => onRemoveCompareSlot(slot.id) : undefined,
      });
    });

    return items;
  }, [
    compareCircuitReslices,
    compareExports,
    compareExportsLoading,
    compareSlots,
    activeTimelineSlotId,
    lapTimingMode,
    onCompareSlotLapChange,
    onRemoveCompareSlot,
    primarySelectedLap,
    primarySessionExport,
    primarySessionId,
    primarySessionLoading,
    primarySlot.laps,
    replay,
    replayLayers,
    sessionTimelineLapSelect,
    sessions,
  ]);
  const cursorTimecodeMs = replay.playheadTimecodeMs;
  const replayLayersRef = useRef(replayLayers);
  replayLayersRef.current = replayLayers;
  const resolveReplayCursorTimecodeMsRef = useRef<
    ((cursor: MapReplayCursor) => number | null) | undefined
  >(undefined);
  resolveReplayCursorTimecodeMsRef.current = (cursor) => {
    const layers = replayLayersRef.current;
    const layer = layers.find((entry) => entry.id === cursor.id);
    if (!layer) {
      return cursor.timecodeMs;
    }

    const alignedElapsedSec = replay.getElapsedSec();
    const elapsedSec = timelineAlignmentMappers.get(layer.id)?.fromAlignedSec(alignedElapsedSec)
      ?? alignedElapsedSec;
    return Math.min(layer.analysis.endMs, layer.analysis.startMs + elapsedSec * 1000);
  };
  const footageReplayWindows = useMemo(
    () =>
      sessionFootage
        .map((upload) => {
          if (!upload.videoUrl || !footageStartConfirmed(upload)) {
            return null;
          }

          const startTimecodeMs = utcIsoToSessionTimecodeMs(
            primarySlot.sessionContext?.lapTimingOverlay,
            upload.startedAtIso,
          );
          const endTimecodeMs = utcIsoToSessionTimecodeMs(
            primarySlot.sessionContext?.lapTimingOverlay,
            upload.endedAtIso,
          );
          if (
            startTimecodeMs == null ||
            endTimecodeMs == null ||
            endTimecodeMs <= startTimecodeMs
          ) {
            return null;
          }

          return { upload, startTimecodeMs, endTimecodeMs };
        })
        .filter(
          (
            entry,
          ): entry is {
            upload: NonNullable<(typeof sessionFootage)[number]>;
            startTimecodeMs: number;
            endTimecodeMs: number;
          } => entry !== null,
        )
        .sort((left, right) => left.startTimecodeMs - right.startTimecodeMs),
    [primarySlot.sessionContext?.lapTimingOverlay, sessionFootage],
  );
  const activeFootageWindow = useMemo(() => {
    if (!replayTimeline) {
      return null;
    }

    const playheadMs = cursorTimecodeMs;

    return (
      footageReplayWindows
        .filter(
          (entry) =>
            playheadMs >= entry.startTimecodeMs &&
            (mediaDurationsSec[entry.upload.uploadSessionId] == null ||
              playheadMs <=
                entry.startTimecodeMs + mediaDurationsSec[entry.upload.uploadSessionId]! * 1000) &&
            replayTimeline.endTimecodeMs >
              Math.max(replayTimeline.startTimecodeMs, entry.startTimecodeMs),
        )
        .sort((left, right) => right.startTimecodeMs - left.startTimecodeMs)[0] ?? null
    );
  }, [cursorTimecodeMs, footageReplayWindows, mediaDurationsSec, replayTimeline]);
  const activeFootageReplayWindow = useMemo(() => {
    if (!replayTimeline || !activeFootageWindow) {
      return null;
    }

    const startTimecodeMs = Math.max(
      replayTimeline.startTimecodeMs,
      activeFootageWindow.startTimecodeMs,
    );
    const endTimecodeMs = replayTimeline.endTimecodeMs;
    return endTimecodeMs > startTimecodeMs ? { startTimecodeMs, endTimecodeMs } : null;
  }, [activeFootageWindow, replayTimeline]);

  const adjustActiveFootageTiming = useCallback(
    async (deltaMs: number) => {
      if (!activeFootageWindow) {
        return;
      }

      const { upload } = activeFootageWindow;
      const startedAtMs = upload.startedAtIso ? Date.parse(upload.startedAtIso) : Number.NaN;
      const endedAtMs = upload.endedAtIso ? Date.parse(upload.endedAtIso) : Number.NaN;
      if (!Number.isFinite(startedAtMs) || !Number.isFinite(endedAtMs)) {
        return;
      }

      setAligningFootageId(upload.uploadSessionId);
      const nextStartedAtIso = new Date(startedAtMs + deltaMs).toISOString();
      const nextEndedAtIso = new Date(endedAtMs + deltaMs).toISOString();
      try {
        await updateGoProUploadSessionMetadata(upload.uploadSessionId, {
          startedAtIso: nextStartedAtIso,
          endedAtIso: nextEndedAtIso,
          videoDurationSec:
            upload.videoDurationSec ?? Math.max(0, (endedAtMs - startedAtMs) / 1000),
          footageTimingSource: 'manual-map',
        });
        await mutateSessionFootage();
      } finally {
        setAligningFootageId(null);
      }
    },
    [activeFootageWindow, mutateSessionFootage],
  );

  const alignActiveFootageFrame = useCallback(
    async (videoCurrentTimeSec: number) => {
      if (!activeFootageWindow || !Number.isFinite(videoCurrentTimeSec)) {
        return;
      }

      const { upload } = activeFootageWindow;
      const durationSec =
        upload.videoDurationSec ??
        (upload.startedAtIso && upload.endedAtIso
          ? Math.max(0, (Date.parse(upload.endedAtIso) - Date.parse(upload.startedAtIso)) / 1000)
          : null);
      const nextStartTimecodeMs = cursorTimecodeMs - videoCurrentTimeSec * 1000;
      const nextStartedAtIso = sessionTimecodeMsToUtcIso(
        primarySlot.sessionContext?.lapTimingOverlay,
        nextStartTimecodeMs,
      );
      if (!nextStartedAtIso) {
        return;
      }

      const startedAtMs = Date.parse(nextStartedAtIso);
      const nextEndedAtIso =
        durationSec != null && Number.isFinite(durationSec)
          ? new Date(startedAtMs + durationSec * 1000).toISOString()
          : (upload.endedAtIso ?? null);

      setAligningFootageId(upload.uploadSessionId);
      try {
        await updateGoProUploadSessionMetadata(upload.uploadSessionId, {
          startedAtIso: nextStartedAtIso,
          endedAtIso: nextEndedAtIso,
          videoDurationSec: durationSec,
          footageTimingSource: 'manual-map',
          footageStartConfirmedAt: new Date().toISOString(),
        });
        await mutateSessionFootage();
      } finally {
        setAligningFootageId(null);
      }
    },
    [
      activeFootageWindow,
      cursorTimecodeMs,
      mutateSessionFootage,
      primarySlot.sessionContext?.lapTimingOverlay,
    ],
  );

  const fullSessionFallbackTrack = useMemo(() => {
    if (trackGroups.length > 0 || !primarySessionExport?.gps.length) {
      return [];
    }

    const first = primarySessionExport.gps[0]!;
    const last = primarySessionExport.gps.at(-1)!;
    if (last.timecodeMs <= first.timecodeMs) {
      return [];
    }

    return buildMapPointsForGpsWindow(
      primarySessionExport,
      first.timecodeMs,
      last.timecodeMs,
      30000,
    );
  }, [primarySessionExport, trackGroups.length]);

  const displayTrackGroups = useMemo(
    () =>
      trackGroups.length > 0
        ? trackGroups
        : fullSessionFallbackTrack.length >= 2
          ? [fullSessionFallbackTrack]
          : [],
    [fullSessionFallbackTrack, trackGroups],
  );

  const compareSpeedRange = useMemo(
    () => buildCompareTrackSpeedRange(displayTrackGroups),
    [displayTrackGroups],
  );

  const idealLineInvalidationKey = useMemo(
    () =>
      [
        idealLineAuthScope ?? 'authenticated',
        primarySlot.sessionContext?.updatedAt ?? 'no-context',
        ...sessions.map((entry) => `${entry.id}:${entry.recordedAtMs ?? 'unknown'}`),
      ].join('|'),
    [idealLineAuthScope, primarySlot.sessionContext?.updatedAt, sessions],
  );
  const {
    data: idealLineReference,
    error: idealLineError,
    isLoading: idealLineLoading,
  } = useSWR(
    !publicDemo || loadIdealLineReference
      ? idealLineReferenceKey(primarySessionId, idealLineInvalidationKey)
      : null,
    ([, activeSessionId]) =>
      loadIdealLineReference
        ? loadIdealLineReference(activeSessionId)
        : fetchIdealLineReference(activeSessionId),
    {
      revalidateOnFocus: true,
      refreshInterval: 60_000,
    },
  );

  const idealPoints = useMemo(() => {
    if (!primarySessionExport || primarySelectedLap == null || !idealLineReference) {
      return [];
    }

    const primaryAnalysis = buildSessionLapAnalysis(
      primarySessionExport,
      primarySelectedLap,
      lapTimingMode,
      primarySlot.circuitLapReslice.activeReslice,
    );
    if (!primaryAnalysis) {
      return [];
    }

    return buildIdealLineMapPoints(
      idealLineReference,
      gpsPointsInWindow(primarySessionExport.gps, primaryAnalysis.startMs, primaryAnalysis.endMs),
    );
  }, [
    idealLineReference,
    lapTimingMode,
    primarySelectedLap,
    primarySessionExport,
    primarySlot.circuitLapReslice.activeReslice,
  ]);
  const showIdealLine = idealPoints.length >= 2;
  const idealLineStatusText =
    publicDemo && !loadIdealLineReference
      ? 'Fastest recorded lap unavailable in this shared session view.'
      : idealLineReferenceStatusText({
          reference: idealLineReference,
          loading: idealLineLoading,
          error: idealLineError,
        });
  const hasTurnSpeedDrawer = Boolean(primaryLayer && primaryLayer.points.length >= 2);
  const { data: detailedCircuitContribution } = useSWR(
    hasTurnSpeedDrawer && resolvedContributionId && !publicDemo
      ? trackContributionDetailKey(resolvedContributionId)
      : null,
    () => fetchTrackContributionById(resolvedContributionId!),
    { revalidateOnFocus: false },
  );
  const detailedGeometry = detailedCircuitContribution?.payload?.detailedGeometry;
  const circuitCorners = detailedCircuitContribution?.payload?.cornerApexSuggestions ?? [];

  const cornerSpeedRows = useMemo(() => {
    if (!turnDrawerOpen || !primaryLayer) {
      return [];
    }

    const primaryBrief = buildLapTelemetryBrief(
      primaryLayer.sessionExport,
      primaryLayer.analysis.lap.lapNumber,
      [],
      measurementPreferences,
      { detailedGeometry, circuitCorners },
    );

    const turnMarkers = (primarySlot.resolvedCircuit.overlay?.mapMarkers ?? [])
      .filter((marker) => marker.kind === 'note')
      .map((marker) => {
        const match = marker.label.match(/(\d+)/);
        const turnNumber = match ? Number.parseInt(match[1]!, 10) : null;
        return Number.isFinite(turnNumber)
          ? { turnNumber, turnLabel: `T${turnNumber}`, lat: marker.lat, lon: marker.lon }
          : null;
      })
      .filter(
        (marker): marker is { turnNumber: number; turnLabel: string; lat: number; lon: number } =>
          marker !== null,
      )
      .sort((left, right) => left.turnNumber - right.turnNumber);

    if (!primaryBrief) {
      if (turnMarkers.length === 0) {
        return [];
      }
    } else if (turnMarkers.length === 0 && primaryBrief.corners.length === 0) {
      return [];
    }

    const compareBriefs = replayLayers.slice(1).map((layer) => ({
      layer,
      brief: buildLapTelemetryBrief(
        layer.sessionExport,
        layer.analysis.lap.lapNumber,
        [],
        measurementPreferences,
        { detailedGeometry, circuitCorners },
      ),
    }));

    const buildCompareCorners = (turnNumber: number, primaryApexKph: number | null) =>
      compareBriefs.map(({ layer, brief }) => {
        const compareCorner =
          brief?.corners.find((entry) => entry.turnNumber === turnNumber) ?? null;
        const compareApexKph = compareCorner?.apexSpeedKph ?? null;
        const apexDeltaKph =
          compareApexKph !== null && primaryApexKph !== null
            ? compareApexKph - primaryApexKph
            : null;

        return {
          layerId: layer.id,
          label: layer.label,
          color: layer.color,
          corner: compareCorner,
          apexDeltaKph,
        };
      });

    if (turnMarkers.length === 0) {
      return (primaryBrief?.corners ?? []).map((corner) => ({
        turnLabel: corner.label,
        primaryCorner: corner,
        compareCorners: buildCompareCorners(corner.turnNumber, corner.apexSpeedKph),
      }));
    }

    return turnMarkers.map((marker) => {
      const primaryCorner =
        primaryBrief?.corners.find((corner) => corner.turnNumber === marker.turnNumber) ?? null;

      return {
        turnLabel: marker.turnLabel,
        primaryCorner,
        compareCorners: buildCompareCorners(marker.turnNumber, primaryCorner?.apexSpeedKph ?? null),
      };
    });
  }, [
    circuitCorners,
    detailedGeometry,
    measurementPreferences,
    primaryLayer,
    primarySlot.resolvedCircuit.overlay?.mapMarkers,
    replayLayers,
    turnDrawerOpen,
  ]);

  useEffect(() => {
    if (!hasTurnSpeedDrawer) {
      setTurnDrawerOpen(false);
    }
  }, [hasTurnSpeedDrawer]);

  const speedUnit = speedUnitLabel(measurementPreferences);
  const formatCornerSpeedValue = (valueKph: number | null) => {
    if (valueKph === null) {
      return '—';
    }

    return formatMeasurement(convertSpeedKph(valueKph, measurementPreferences), 0);
  };

  const formatCornerTriplet = (
    entryKph: number | null,
    apexKph: number | null,
    exitKph: number | null,
  ) =>
    `${formatCornerSpeedValue(entryKph)}/${formatCornerSpeedValue(apexKph)}/${formatCornerSpeedValue(exitKph)}`;

  const formatApexDelta = (deltaKph: number | null) =>
    deltaKph === null
      ? '—'
      : `${deltaKph >= 0 ? '+' : ''}${formatMeasurement(convertSpeedKph(deltaKph, measurementPreferences), 1)}`;

  const driverLabelsBySessionId = useMemo(() => {
    const labels = new Map<string, string>();
    for (const session of sessions) {
      const driverName = session.driverName?.trim();
      labels.set(session.id, driverName || 'Driver unassigned');
    }
    return labels;
  }, [sessions]);

  const driverAvatarsBySessionId = useMemo(() => {
    const avatars = new Map<string, string | null>();
    for (const session of sessions) {
      avatars.set(session.id, session.driverAvatarUrl ?? null);
    }
    return avatars;
  }, [sessions]);

  const layerIdentityById = useMemo(() => {
    const identityCounts = new Map<string, number>();
    const identities = new Map<
      string,
      {
        avatarUrl: string | null;
        initials: string;
        variant: number;
      }
    >();

    for (const layer of replayLayers) {
      const driverLabel = layer.sessionId
        ? (driverLabelsBySessionId.get(layer.sessionId) ?? 'Driver unassigned')
        : 'Driver unassigned';
      const avatarUrl = layer.sessionId
        ? (driverAvatarsBySessionId.get(layer.sessionId) ?? null)
        : null;
      const key =
        driverLabel === 'Driver unassigned'
          ? `layer:${layer.id}`
          : `driver:${driverLabel}:${avatarUrl ?? ''}`;
      const variant = identityCounts.get(key) ?? 0;
      identityCounts.set(key, variant + 1);
      identities.set(layer.id, {
        avatarUrl,
        initials: driverInitials(driverLabel),
        variant,
      });
    }

    return identities;
  }, [driverAvatarsBySessionId, driverLabelsBySessionId, replayLayers]);

  const legendTrackItems = useMemo(() => {
    const layerDrivers = replayLayers.map((layer) =>
      layer.sessionId
        ? (driverLabelsBySessionId.get(layer.sessionId) ?? 'Driver unassigned')
        : 'Driver unassigned',
    );
    const hasMultipleDrivers = new Set(layerDrivers).size > 1;

    return replayLayers.map((layer, index) => {
      const driverLabel = layerDrivers[index];
      return {
        label: hasMultipleDrivers && driverLabel ? `${layer.label} | ${driverLabel}` : layer.label,
        color: layer.color,
      };
    });
  }, [driverLabelsBySessionId, replayLayers]);

  const replayCursors = useMemo((): MapReplayCursor[] => {
    if (!primaryLayer || replayLayers.length === 0) {
      return [];
    }

    return replayLayers.filter((layer) => layer.points.length >= 2).map((layer) => ({
      id: layer.id,
      points: layer.points,
      // Placeholder — live position comes from resolveReplayCursorTimecodeMsRef during replay.
      timecodeMs: layer.analysis.startMs,
      color: layer.color,
      avatarUrl: layerIdentityById.get(layer.id)?.avatarUrl ?? null,
      initials: layerIdentityById.get(layer.id)?.initials ?? null,
      identityVariant: layerIdentityById.get(layer.id)?.variant ?? 0,
      label: layer.label,
      follow: layer.id === 'primary',
    }));
  }, [layerIdentityById, primaryLayer, replayLayers]);

  const circuitOverlay = primarySlot.resolvedCircuit.overlay;
  const mapPoints = displayTrackGroups[0] ?? [];
  const footageStartMarkers = useMemo((): TrackMapMarker[] => {
    if (!primaryLayer?.points.length) {
      return [];
    }

    return footageReplayWindows
      .map((entry): TrackMapMarker | null => {
        const point = interpolateMapPointAtTime(primaryLayer.points, entry.startTimecodeMs);
        if (!point) {
          return null;
        }

        return {
          id: `footage-start-${entry.upload.uploadSessionId}`,
          kind: 'footage-start' as const,
          label: '',
          lat: point.lat,
          lon: point.lon,
          x: point.x,
          y: point.y,
          color:
            entry.upload.uploadSessionId === activeFootageWindow?.upload.uploadSessionId
              ? '#34D399'
              : '#10B981',
          selected: entry.upload.uploadSessionId === activeFootageWindow?.upload.uploadSessionId,
        };
      })
      .filter((marker): marker is TrackMapMarker => marker !== null);
  }, [activeFootageWindow?.upload.uploadSessionId, footageReplayWindows, primaryLayer?.points]);
  const mapMarkers = useMemo(
    () => [...(circuitOverlay?.mapMarkers ?? []), ...footageStartMarkers],
    [circuitOverlay?.mapMarkers, footageStartMarkers],
  );
  const requiredCircuitOverlayKey = useMemo(() => {
    if (primarySlot.contributionsLoading || !circuitOverlay?.startFinishLine) {
      return null;
    }

    const { pointA, pointB } = circuitOverlay.startFinishLine;
    return [
      resolvedContributionId ?? 'circuit',
      pointA.lat,
      pointA.lon,
      pointB.lat,
      pointB.lon,
      ...(circuitOverlay.mapMarkers ?? []).map(
        (marker) => `${marker.id}:${marker.lat}:${marker.lon}`,
      ),
    ].join(':');
  }, [
    circuitOverlay?.mapMarkers,
    circuitOverlay?.startFinishLine,
    primarySlot.contributionsLoading,
    resolvedContributionId,
  ]);
  const handleMapOverlaysReady = useCallback(() => {
    if (requiredCircuitOverlayKey) {
      setRenderedCircuitOverlayKey(requiredCircuitOverlayKey);
    }
  }, [requiredCircuitOverlayKey]);
  const hasReferenceTrack = Boolean(
    circuitOverlay?.referenceTrackGroups.some((group) => group.length >= 2),
  );
  const showTraceDrawer = Boolean(replayTimeline && replayLayers.length > 0 && primaryLayer);
  const hasInlineVideo = Boolean(
    activeFootageWindow?.upload.videoUrl && activeFootageReplayWindow,
  );
  const showInlineRightPane = hasInlineVideo;
  const handleFootageStartMapClick = useCallback(
    (coord: { lat: number; lon: number }) => {
      if (!footageStartPickActive || !onFootageStartPointPick || !primaryLayer?.points.length) {
        return;
      }

      let nearest = primaryLayer.points[0]!;
      let nearestDistance = Number.POSITIVE_INFINITY;
      for (const point of primaryLayer.points) {
        const distance = (point.lat - coord.lat) ** 2 + (point.lon - coord.lon) ** 2;
        if (distance < nearestDistance) {
          nearest = point;
          nearestDistance = distance;
        }
      }

      onFootageStartPointPick(nearest);
    },
    [footageStartPickActive, onFootageStartPointPick, primaryLayer?.points],
  );
  const handleMapMarkerClick = useCallback(
    (markerId: string) => {
      if (!markerId.startsWith('footage-start-')) {
        return;
      }

      const uploadSessionId = markerId.slice('footage-start-'.length);
      const footageWindow = footageReplayWindows.find(
        (entry) => entry.upload.uploadSessionId === uploadSessionId,
      );
      if (!footageWindow) {
        return;
      }

      replay.seekTimecode(footageWindow.startTimecodeMs);
      replay.play();
    },
    [footageReplayWindows, replay],
  );
  const handleFootageMediaDuration = useCallback((uploadSessionId: string, durationSec: number) => {
    if (!Number.isFinite(durationSec) || durationSec <= 0) {
      return;
    }

    setMediaDurationsSec((current) =>
      current[uploadSessionId] === durationSec
        ? current
        : { ...current, [uploadSessionId]: durationSec },
    );
  }, []);
  const replayDataLoading =
    !publicDemo && Boolean(primarySessionId) && (!primarySessionExport || compareExportsLoading);
  const replayControlsDisabled =
    replayDataLoading ||
    (reviewView === 'traces' && !mapDisplay.basemap
      ? false
      : !requiredCircuitOverlayKey || renderedCircuitOverlayKey !== requiredCircuitOverlayKey);

  useEffect(() => {
    if (replayControlsDisabled && replay.isPlaying) {
      replay.pause();
    }
  }, [replay.isPlaying, replay.pause, replayControlsDisabled]);

  const hasMapContent = displayTrackGroups.length > 0 || hasReferenceTrack;
  const sessionExportLoading =
    !publicDemo && Boolean(primarySessionId) && !primarySessionExport;
  const mapStillPreparing =
    !publicDemo &&
    Boolean(primarySessionExport) &&
    !hasMapContent &&
    (primarySlot.contributionsLoading || compareExportsLoading);
  const showFullMapLoading = sessionExportLoading || mapStillPreparing;
  const mapHasNoGps =
    !publicDemo && Boolean(primarySessionExport) && !hasMapContent && !showFullMapLoading;
  const showCompareLoadingBar =
    !publicDemo && compareExportsLoading && hasMapContent && !showFullMapLoading;
  const mapLoadingLabel = sessionExportLoading
    ? 'Loading session telemetry…'
    : primarySlot.contributionsLoading
      ? 'Loading circuit data…'
      : compareExportsLoading
        ? 'Loading compare sessions…'
        : 'Preparing lap traces…';
  const showReplayBar = Boolean(primarySessionExport && primarySelectedLap != null);
  const effectiveMapDisplay =
    reviewView === 'traces' ? mapDisplay : DEFAULT_SESSION_MAP_DISPLAY;
  const lineOnlyTraceView = reviewView === 'traces' && !effectiveMapDisplay.basemap;
  const mapDockDark = isDark || (reviewView === 'traces' && !effectiveMapDisplay.basemap);

  useEffect(() => {
    sessionMapDebug('map loading state', {
      sessionId: primarySessionId,
      sessionExportLoading,
      mapStillPreparing,
      showFullMapLoading,
      hasMapContent,
      mapHasNoGps,
      trackGroupCount: displayTrackGroups.length,
      mapPointCount: mapPoints.length,
      primarySessionLoading,
      primarySlotContributionsLoading: primarySlot.contributionsLoading,
      compareExportsLoading,
      replayDataLoading,
      mapLoadingLabel,
    });
  }, [
    compareExportsLoading,
    displayTrackGroups.length,
    hasMapContent,
    mapHasNoGps,
    mapLoadingLabel,
    mapPoints.length,
    mapStillPreparing,
    primarySessionId,
    primarySessionLoading,
    primarySlot.contributionsLoading,
    replayDataLoading,
    sessionExportLoading,
    showFullMapLoading,
  ]);

  if (!primarySessionId) {
    return (
      <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', bgcolor: isDark ? '#020A12' : '#F8FAFC' }}>
        <Typography variant="body2" color="text.secondary">
          Choose a session to view lap traces on the map.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: isDark ? '#020A12' : '#F8FAFC',
        }}
      >
        <SessionComparisonRail
          items={comparisonLapItems}
          isDark={isDark}
          onManage={onOpenSessionSelector}
        />
        <Box
          ref={traceLayoutRef}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            columnGap: 0,
            rowGap: reviewView === 'traces' ? 1 : 0,
            p: reviewView === 'traces' ? 1 : 0,
            gridTemplateColumns: reviewView === 'traces'
              ? {
                  xs: '1fr',
                  lg: `minmax(0, 1fr) 12px minmax(${MIN_TRACE_MAP_PANE_WIDTH}px, ${mapPaneWidth}px)`,
                }
              : '1fr',
            gridTemplateRows: reviewView === 'traces'
              ? {
                  xs: 'minmax(340px, 1fr) minmax(250px, 0.75fr) auto auto',
                  lg: showInlineRightPane
                    ? 'minmax(260px, 1fr) minmax(210px, 0.55fr) auto'
                    : 'minmax(0, 1fr) auto',
                }
              : 'minmax(0, 1fr)',
            gridTemplateAreas: reviewView === 'traces'
              ? {
                  xs: '"traces" "map" "video" "storyboard"',
                  lg: showInlineRightPane
                    ? '"traces divider map" "traces divider video" "storyboard storyboard storyboard"'
                    : '"traces divider map" "storyboard storyboard storyboard"',
                }
              : '"map"',
            overflow: 'auto',
          }}
        >
        <Box
          ref={mapContainerRef}
          data-testid="session-map-panel"
          sx={{ position: 'relative', gridArea: 'map', minHeight: 0, width: '100%', overflow: 'hidden', borderRadius: reviewView === 'traces' ? 1.5 : 0, border: reviewView === 'traces' ? '1px solid rgba(148,163,184,0.18)' : 0 }}
        >
          {hasMapContent ? (
            <SessionsCompareMapTrack
              showBasemap={effectiveMapDisplay.basemap}
              showTrackAnnotations={effectiveMapDisplay.trackAnnotations}
              viewKey={viewKey ?? primarySessionId ?? 'session'}
              mapPoints={mapPoints}
              displayTrackGroups={displayTrackGroups}
              referenceTrackGroups={effectiveMapDisplay.trackAnnotations ? (circuitOverlay?.referenceTrackGroups ?? []) : []}
              mapMarkers={effectiveMapDisplay.trackAnnotations ? mapMarkers : []}
              startFinishLine={effectiveMapDisplay.trackAnnotations ? circuitOverlay?.startFinishLine : undefined}
              compareSpeedRange={compareSpeedRange}
              idealPoints={effectiveMapDisplay.trackAnnotations ? idealPoints : []}
              showIdealLine={effectiveMapDisplay.trackAnnotations && showIdealLine}
              legendTrackItems={legendTrackItems}
              cursorTimecodeMs={!replay.isPlaying && !lineOnlyTraceView ? cursorTimecodeMs : null}
              replayCursors={lineOnlyTraceView && !replay.isPlaying ? [] : replayCursors}
              resolveReplayCursorTimecodeMsRef={resolveReplayCursorTimecodeMsRef}
              followReplayCursor={
                replay.isPlaying && (effectiveMapDisplay.trackAnnotations || lineOnlyTraceView)
              }
              onOverlaysReady={handleMapOverlaysReady}
              onMapLatLngClick={footageStartPickActive ? handleFootageStartMapClick : undefined}
              onMapMarkerClick={handleMapMarkerClick}
              brakingOverlayEnabled={brakingOverlayEnabled}
              coastingOverlayEnabled={coastingOverlayEnabled}
            />
          ) : mapHasNoGps ? (
            <Box sx={{ flex: 1, minHeight: 0, display: 'grid', placeItems: 'center', px: 3 }}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No GPS lap traces are available for this session yet.
              </Typography>
            </Box>
          ) : null}

          {reviewView === 'traces' ? (
            <>
              <IconButton
                aria-label="Map display settings"
                title="Map display settings"
                size="small"
                onClick={(event) => setMapDisplayMenuAnchor(event.currentTarget)}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: mapDisplay.basemap ? 56 : 12,
                  zIndex: 10,
                  width: 34,
                  height: 34,
                  color: '#F8FAFC',
                  bgcolor: 'rgba(2,10,18,0.88)',
                  border: '1px solid rgba(148,163,184,0.42)',
                  boxShadow: '0 8px 22px rgba(0,0,0,0.3)',
                  '&:hover': { bgcolor: 'rgba(15,23,42,0.96)' },
                }}
              >
                <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Menu
                anchorEl={mapDisplayMenuAnchor}
                open={Boolean(mapDisplayMenuAnchor)}
                onClose={() => setMapDisplayMenuAnchor(null)}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      minWidth: 290,
                      bgcolor: isDark ? 'rgba(2,10,18,0.98)' : '#FFFFFF',
                      color: isDark ? '#F8FAFC' : '#111827',
                      border: `1px solid ${isDark ? 'rgba(148,163,184,0.28)' : '#E5E7EB'}`,
                      boxShadow: '0 16px 36px rgba(0,0,0,0.32)',
                    },
                  },
                }}
              >
                <Box sx={{ px: 1.5, pt: 0.65, pb: 0.8 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 900 }}>Map display</Typography>
                  <Typography sx={{ mt: 0.15, fontSize: '0.64rem', color: 'text.secondary' }}>
                    Choose the map, cards, and controls shown with lap traces.
                  </Typography>
                </Box>
                <MenuItem
                  onClick={() => {
                    updateMapDisplay({
                      basemap: false,
                      telemetryCards: false,
                      replayDock: true,
                      trackAnnotations: false,
                    });
                    setMapDisplayMenuAnchor(null);
                  }}
                  sx={{ minHeight: 52 }}
                >
                  <ListItemText
                    primary="GPS line only"
                    secondary="Hide the map, cards, and annotations; keep playback"
                    primaryTypographyProps={{ fontSize: '0.76rem', fontWeight: 900 }}
                    secondaryTypographyProps={{ fontSize: '0.62rem' }}
                  />
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    updateMapDisplay(DEFAULT_SESSION_MAP_DISPLAY);
                    setMapDisplayMenuAnchor(null);
                  }}
                  sx={{ minHeight: 46 }}
                >
                  <ListItemText
                    primary="Restore default display"
                    primaryTypographyProps={{ fontSize: '0.74rem', fontWeight: 800 }}
                  />
                </MenuItem>
                {([
                  ['basemap', 'Satellite map', 'Show map imagery behind the GPS line'],
                  ['telemetryCards', 'Telemetry cards', 'Show live speed, steering, and load'],
                  ['replayDock', 'Replay controls', 'Show the legend and replay controls'],
                  ['trackAnnotations', 'Track annotations', 'Show turns, braking zones, and reference lines'],
                ] as const).map(([key, label, description]) => (
                  <MenuItem
                    key={key}
                    onClick={() => updateMapDisplay({ [key]: !mapDisplay[key] })}
                    sx={{ minHeight: 52 }}
                  >
                    <ListItemText
                      primary={label}
                      secondary={description}
                      primaryTypographyProps={{ fontSize: '0.74rem', fontWeight: 800 }}
                      secondaryTypographyProps={{ fontSize: '0.62rem' }}
                    />
                    <Switch
                      size="small"
                      checked={mapDisplay[key]}
                      tabIndex={-1}
                      inputProps={{ 'aria-label': label }}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : null}

          {showFullMapLoading ? <SessionMapLoadingOverlay label={mapLoadingLabel} /> : null}
          {showCompareLoadingBar ? (
            <SessionMapLoadingOverlay label="Loading compare sessions…" compact />
          ) : null}

          {reviewView === 'map' && activeFootageWindow?.upload.videoUrl && activeFootageReplayWindow ? (
            <GoProSyncedVideoOverlay
              key={activeFootageWindow.upload.uploadSessionId}
              videoSrc={activeFootageWindow.upload.videoUrl}
              replay={replay}
              containerEl={mapContainerRef.current}
              clipStartTimecodeMs={activeFootageReplayWindow.startTimecodeMs}
              videoStartTimecodeMs={activeFootageWindow.startTimecodeMs}
              storageKey="kangaroo.session-footage-video-overlay-layout"
              title={activeFootageWindow.upload.originalName}
              onAdjustTimingMs={adjustActiveFootageTiming}
              onAlignCurrentFrame={alignActiveFootageFrame}
              onVideoDuration={(durationSec) =>
                handleFootageMediaDuration(activeFootageWindow.upload.uploadSessionId, durationSec)
              }
              timingAdjustmentDisabled={
                aligningFootageId === activeFootageWindow.upload.uploadSessionId
              }
            />
          ) : null}

          {footageStartPickActive ? (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: 12,
                transform: 'translateX(-50%)',
                zIndex: 5,
                px: 1.35,
                py: 0.75,
                borderRadius: '6px',
                bgcolor: 'rgba(1,13,24,0.9)',
                border: '1px solid rgba(52,211,153,0.55)',
                boxShadow: '0 10px 26px rgba(0,0,0,0.34)',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.76rem',
                  fontWeight: 850,
                  color: '#D1FAE5',
                  whiteSpace: 'nowrap',
                }}
              >
                Click the trace where this footage starts
              </Typography>
            </Box>
          ) : null}

          {effectiveMapDisplay.telemetryCards && primarySessionExport && primarySelectedLap != null && replayLayers.length > 0 ? (
            <SessionsCompareMapSpeedHud
              alignmentMappers={timelineAlignmentMappers}
              replay={replay}
              replayLayers={replayLayers}
              layerIdentityById={layerIdentityById}
              actionsSlot={
                showTraceDrawer || hasTurnSpeedDrawer ? (
                  <Stack
                    direction="row"
                    spacing={0.7}
                    sx={{ display: { xs: 'flex', sm: 'none' }, pointerEvents: 'auto' }}
                  >
                    {replayLayers.length > 0 ? (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<TuneOutlinedIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setKartSetupsOpen(true)}
                        sx={{
                          minHeight: 34,
                          px: 1.2,
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontSize: '0.74rem',
                          fontWeight: 850,
                          bgcolor: '#111827',
                          color: '#FFFFFF',
                          '&:hover': { bgcolor: '#1F2937' },
                        }}
                      >
                        Setups
                      </Button>
                    ) : null}
                    {showTraceDrawer ? (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<LayersOutlinedIcon sx={{ fontSize: 17 }} />}
                        onClick={(event) => setOverlayMenuAnchor(event.currentTarget)}
                        sx={{
                          minHeight: 34,
                          px: 1.2,
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontSize: '0.74rem',
                          fontWeight: 850,
                          bgcolor: traceDrawerOpen ? '#7C3AED' : '#8B5CF6',
                          color: '#FFFFFF',
                          '&:hover': { bgcolor: '#7C3AED' },
                        }}
                      >
                        Overlays
                      </Button>
                    ) : null}
                    {hasTurnSpeedDrawer ? (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<RouteIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setTurnDrawerOpen((open) => !open)}
                        sx={{
                          minHeight: 34,
                          px: 1.2,
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontSize: '0.74rem',
                          fontWeight: 850,
                          bgcolor: turnDrawerOpen ? '#7C3AED' : '#8B5CF6',
                          color: '#FFFFFF',
                          '&:hover': { bgcolor: '#7C3AED' },
                        }}
                      >
                        Turns
                      </Button>
                    ) : null}
                  </Stack>
                ) : null
              }
            />
          ) : null}

          {effectiveMapDisplay.replayDock && idealLineStatusText ? (
            <Box sx={mapPlaybackDockOuterSx(mapDockDark)}>
              <Box sx={mapPlaybackDockShellSx(mapDockDark)}>
                {effectiveMapDisplay.basemap && (legendTrackItems.length > 0 || showIdealLine) ? (
                  <Box
                    sx={{
                      ...mapPlaybackDockLegendSx(mapDockDark),
                      ...(showReplayBar ? null : { borderBottom: 'none' }),
                    }}
                  >
                    <TrackMapLegend
                      trackItems={legendTrackItems}
                      showIdealLine={showIdealLine}
                      variant="dock"
                      sx={{
                        pt: 0,
                        pb: 0,
                        px: 0,
                        justifyContent: 'center',
                        ...(mapDockDark
                          ? { '& .MuiTypography-root': { color: '#CBD5E1' } }
                          : null),
                      }}
                    />
                  </Box>
                ) : null}
                {effectiveMapDisplay.basemap ? (
                  <Typography
                    role="status"
                    aria-live="polite"
                    sx={{
                      px: 1.25,
                      py: 0.55,
                      maxWidth: { xs: '82vw', sm: 680 },
                      color: mapDockDark
                        ? showIdealLine ? '#BAE6FD' : '#CBD5E1'
                        : showIdealLine ? '#1E3A5F' : '#475569',
                      fontSize: { xs: '0.68rem', sm: '0.72rem' },
                      fontWeight: 700,
                      lineHeight: 1.35,
                      textAlign: 'center',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {idealLineStatusText}
                  </Typography>
                ) : null}
                {showReplayBar ? (
                  <Box sx={mapPlaybackDockReplaySx()}>
                    <TelemetryReplayBar
                      replay={replay}
                      scope="lap"
                      onScopeChange={() => {}}
                      lapNumber={primarySelectedLap!}
                      totalLaps={1}
                      lapScopeLabel={`L${primarySelectedLap} | ${replayLayers.length > 1 ? `${replayLayers.length} laps` : 'lap replay'}`}
                      compact
                      mini
                      simple
                      showScopeToggle={false}
                      controlsDisabled={replayControlsDisabled}
                      forceDark={mapDockDark}
                    />
                  </Box>
                ) : null}
              </Box>
            </Box>
          ) : null}

          {primarySessionId && reviewView === 'map' ? (
            <Box
              sx={{
                position: 'absolute',
                right: 12,
                top: 12,
                zIndex: 3,
                display: { xs: 'none', sm: 'flex' },
                gap: 0.7,
                alignItems: 'center',
                maxWidth: 'calc(100% - 24px)',
              }}
            >
              {replayLayers.length > 0 ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<TuneOutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setKartSetupsOpen(true)}
                  sx={{
                    height: 30,
                    px: 1.1,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontSize: '0.74rem',
                    fontWeight: 850,
                    bgcolor: 'rgba(1,13,24,0.82)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(36,62,82,0.72)',
                    boxShadow: '0 8px 22px rgba(0,0,0,0.3)',
                    flexShrink: 0,
                    '&:hover': { bgcolor: 'rgba(12,27,42,0.94)' },
                  }}
                >
                  Kart setups
                </Button>
              ) : null}
              {onOpenLapMeta ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                  disabled={lapMetaDisabled}
                  onClick={onOpenLapMeta}
                  sx={{
                    height: 30,
                    px: 1.1,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontSize: '0.74rem',
                    fontWeight: 850,
                    bgcolor: 'rgba(1,13,24,0.82)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(36,62,82,0.72)',
                    boxShadow: '0 8px 22px rgba(0,0,0,0.3)',
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: 'rgba(12,27,42,0.94)',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.48)',
                      bgcolor: 'rgba(1,13,24,0.62)',
                    },
                  }}
                >
                  Lap meta
                </Button>
              ) : null}

              {onOpenFootage ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<VideoFileOutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={onOpenFootage}
                  sx={{
                    height: 30,
                    px: 1.1,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontSize: '0.74rem',
                    fontWeight: 850,
                    bgcolor: 'rgba(1,13,24,0.82)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(36,62,82,0.72)',
                    boxShadow: '0 8px 22px rgba(0,0,0,0.3)',
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: 'rgba(12,27,42,0.94)',
                    },
                  }}
                >
                  Footage
                </Button>
              ) : null}

              {showTraceDrawer ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    replayDataLoading && !showTraceDrawer ? (
                      <CircularProgress size={14} thickness={5} sx={{ color: '#FFFFFF' }} />
                    ) : (
                      <LayersOutlinedIcon sx={{ fontSize: 17 }} />
                    )
                  }
                  disabled={!showTraceDrawer}
                  onClick={(event) => setOverlayMenuAnchor(event.currentTarget)}
                  sx={{
                    height: 30,
                    px: 1.1,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontSize: '0.74rem',
                    fontWeight: 850,
                    bgcolor: traceDrawerOpen ? 'rgba(183,140,255,0.88)' : 'rgba(1,13,24,0.82)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(36,62,82,0.72)',
                    boxShadow: '0 8px 22px rgba(0,0,0,0.3)',
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: traceDrawerOpen ? 'rgba(183,140,255,0.96)' : 'rgba(12,27,42,0.94)',
                    },
                  }}
                >
                  {replayDataLoading && !showTraceDrawer ? 'Loading' : 'Overlays'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    replayDataLoading ? (
                      <CircularProgress size={14} thickness={5} sx={{ color: '#FFFFFF' }} />
                    ) : (
                      <ShowChartIcon sx={{ fontSize: 17 }} />
                    )
                  }
                  disabled
                  sx={{
                    height: 30,
                    px: 1.1,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontSize: '0.74rem',
                    fontWeight: 850,
                    bgcolor: 'rgba(1,13,24,0.72)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(36,62,82,0.72)',
                    boxShadow: '0 8px 22px rgba(0,0,0,0.3)',
                    flexShrink: 0,
                    '&.Mui-disabled': {
                      color: '#FFFFFF',
                      bgcolor: 'rgba(1,13,24,0.72)',
                    },
                  }}
                >
                  {replayDataLoading ? 'Loading' : 'Lap traces'}
                </Button>
              )}

              {hasTurnSpeedDrawer || replayDataLoading ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    replayDataLoading && !hasTurnSpeedDrawer ? (
                      <CircularProgress size={14} thickness={5} sx={{ color: '#FFFFFF' }} />
                    ) : (
                      <RouteIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  disabled={!hasTurnSpeedDrawer}
                  onClick={() => setTurnDrawerOpen((open) => !open)}
                  sx={{
                    height: 30,
                    px: 1.1,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '0.74rem',
                    fontWeight: 850,
                    bgcolor: turnDrawerOpen ? 'rgba(183,140,255,0.88)' : 'rgba(1,13,24,0.82)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(36,62,82,0.72)',
                    boxShadow: '0 8px 22px rgba(0,0,0,0.3)',
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: turnDrawerOpen ? 'rgba(183,140,255,0.96)' : 'rgba(12,27,42,0.94)',
                    },
                    '&.Mui-disabled': {
                      color: '#FFFFFF',
                      bgcolor: 'rgba(1,13,24,0.72)',
                    },
                  }}
                >
                  {replayDataLoading && !hasTurnSpeedDrawer ? 'Loading' : 'Turns'}
                </Button>
              ) : null}
            </Box>
          ) : null}

          <Menu
            anchorEl={overlayMenuAnchor}
            open={Boolean(overlayMenuAnchor)}
            onClose={() => setOverlayMenuAnchor(null)}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.5,
                  minWidth: 270,
                  bgcolor: 'rgba(2,10,18,0.98)',
                  color: '#F8FAFC',
                  border: '1px solid rgba(148,163,184,0.28)',
                  boxShadow: '0 18px 42px rgba(0,0,0,0.5)',
                },
              },
            }}
          >
            <MenuItem
              disabled={!showTraceDrawer}
              onClick={() => {
                setTraceDrawerOpen((open) => !open);
                setOverlayMenuAnchor(null);
              }}
              sx={{ minHeight: 52 }}
            >
              <ListItemIcon>
                <ShowChartIcon
                  sx={{ fontSize: 19, color: traceDrawerOpen ? '#A78BFA' : '#94A3B8' }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Lap traces"
                secondary="Speed, RPM, and comparison charts"
                primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 850 }}
                secondaryTypographyProps={{ fontSize: '0.64rem', color: '#94A3B8' }}
              />
              <Switch size="small" checked={traceDrawerOpen} tabIndex={-1} />
            </MenuItem>
            <MenuItem
              onClick={() => updateBrakingOverlay(!brakingOverlayEnabled)}
              sx={{ minHeight: 52 }}
            >
              <ListItemIcon>
                <LocalFireDepartmentOutlinedIcon
                  sx={{ fontSize: 19, color: brakingOverlayEnabled ? '#FBBF24' : '#94A3B8' }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Braking zones"
                secondary="Soft and hard deceleration on the map"
                primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 850 }}
                secondaryTypographyProps={{ fontSize: '0.64rem', color: '#94A3B8' }}
              />
              <Switch size="small" checked={brakingOverlayEnabled} tabIndex={-1} />
            </MenuItem>
            <MenuItem
              onClick={() => updateCoastingOverlay(!coastingOverlayEnabled)}
              sx={{ minHeight: 52 }}
            >
              <ListItemIcon>
                <AirOutlinedIcon
                  sx={{ fontSize: 19, color: coastingOverlayEnabled ? '#38BDF8' : '#94A3B8' }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Estimated coasting"
                secondary="Sustained mild deceleration from GPS speed"
                primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 850 }}
                secondaryTypographyProps={{ fontSize: '0.64rem', color: '#94A3B8' }}
              />
              <Switch size="small" checked={coastingOverlayEnabled} tabIndex={-1} />
            </MenuItem>
          </Menu>
        </Box>

        {reviewView === 'traces' ? (
          <Box
            role="separator"
            aria-label="Resize map panel"
            aria-orientation="vertical"
            aria-valuemin={MIN_TRACE_MAP_PANE_WIDTH}
            aria-valuemax={1200}
            aria-valuenow={mapPaneWidth}
            aria-valuetext={`${mapPaneWidth} pixels wide`}
            tabIndex={0}
            title="Drag to resize the map. Double-click or press Home to reset."
            onPointerDown={(event) => {
              event.preventDefault();
              event.currentTarget.setPointerCapture(event.pointerId);
              mapPaneResizeStartRef.current = {
                pointerId: event.pointerId,
                clientX: event.clientX,
                width: mapPaneWidth,
              };
              setMapPaneResizing(true);
            }}
            onPointerMove={(event) => {
              const start = mapPaneResizeStartRef.current;
              if (!start || start.pointerId !== event.pointerId) return;
              event.preventDefault();
              updateMapPaneWidth(start.width - (event.clientX - start.clientX));
            }}
            onPointerUp={(event) => {
              if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
              }
              mapPaneResizeStartRef.current = null;
              setMapPaneResizing(false);
            }}
            onPointerCancel={() => {
              mapPaneResizeStartRef.current = null;
              setMapPaneResizing(false);
            }}
            onDoubleClick={(event) => {
              event.preventDefault();
              resetMapPaneWidth();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Home') {
                event.preventDefault();
                resetMapPaneWidth();
                return;
              }
              if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
              event.preventDefault();
              const step = event.shiftKey ? 32 : 12;
              updateMapPaneWidth(
                mapPaneWidth + (event.key === 'ArrowLeft' ? step : -step),
              );
            }}
            sx={{
              gridArea: 'divider',
              display: { xs: 'none', lg: 'flex' },
              minWidth: 12,
              minHeight: 0,
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'col-resize',
              touchAction: 'none',
              outline: 0,
              '&:focus-visible > div': {
                bgcolor: '#8B5CF6',
                boxShadow: '0 0 0 3px rgba(139,92,246,0.2)',
              },
            }}
          >
            <Box
              sx={{
                width: 3,
                height: 56,
                borderRadius: 999,
                bgcolor: mapPaneResizing
                  ? '#8B5CF6'
                  : isDark
                    ? 'rgba(148,163,184,0.42)'
                    : 'rgba(100,116,139,0.32)',
                transition: mapPaneResizing ? 'none' : 'background-color 140ms ease',
              }}
            />
          </Box>
        ) : null}

        {reviewView === 'traces' && showTraceDrawer && primaryLayer ? (
          <Box
            data-testid="session-trace-workspace"
            sx={{
              gridArea: 'traces',
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: 0,
              borderRadius: 0,
              bgcolor: 'transparent',
            }}
          >
            <Stack
              direction="row"
              sx={{
                ...traceDrawerHeaderSx(isDark),
                flexShrink: 0,
                bgcolor: 'transparent',
                px: 0.5,
              }}
            >
              <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', minWidth: 0 }}>
                <ShowChartIcon sx={{ fontSize: 18, color: traceChrome.iconAccent, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.86rem', fontWeight: 900, color: traceChrome.title }}>Lap traces</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 750, color: traceChrome.subtitle }}>
                  {primaryLayer.label} | {replayLayers.length > 1 ? `${replayLayers.length} laps` : 'lap replay'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.6} sx={{ ml: 'auto', flexShrink: 0 }}>
                <Button
                  size="small"
                  variant={tracePickerOpen ? 'contained' : 'outlined'}
                  startIcon={<RouteIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setTracePickerOpen((open) => !open)}
                  sx={{ height: 30, px: 1, borderRadius: '6px', textTransform: 'none', fontSize: '0.7rem', fontWeight: 850, color: tracePickerOpen && !isDark ? '#111827' : isDark ? '#FFFFFF' : '#374151', borderColor: traceChrome.tracesButtonBorder, bgcolor: tracePickerOpen ? traceChrome.tracesButtonActiveBg : traceChrome.tracesButtonBg }}
                >
                  Choose traces
                </Button>
                <IconButton
                  aria-label="More trace actions"
                  size="small"
                  onClick={(event) => setTraceActionsMenuAnchor(event.currentTarget)}
                  sx={{
                    width: 30,
                    height: 30,
                    color: isDark ? '#FFFFFF' : '#475569',
                    border: `1px solid ${traceChrome.tracesButtonBorder}`,
                    borderRadius: '6px',
                  }}
                >
                  <MoreHorizIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <SessionCompareTracePanelLive
                replay={replay}
                layers={replayLayers}
                durationSec={replay.durationSec}
                alignmentMode={traceAlignmentMode}
                onTraceZoomRangeChange={setTraceZoomRange}
                fillHeight
                tracePickerOpen={tracePickerOpen}
                onTracePickerClose={() => setTracePickerOpen(false)}
                saveDialogOpen={traceSaveDialogOpen}
                onSaveDialogClose={() => setTraceSaveDialogOpen(false)}
                onSeekSec={(sec) => {
                  replay.pause();
                  replay.seekElapsed(sec);
                }}
              />
            </Box>
          </Box>
        ) : reviewView === 'traces' ? (
          <Box data-testid="session-trace-workspace" sx={{ gridArea: 'traces', minHeight: 0, display: 'grid', placeItems: 'center', border: 0, borderRadius: 0, bgcolor: 'transparent' }}>
            <Stack spacing={1} sx={{ alignItems: 'center' }}>
              <CircularProgress size={24} thickness={4.5} sx={{ color: isDark ? '#94A3B8' : '#64748B' }} />
              <Typography sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.74rem', fontWeight: 750 }}>Preparing telemetry traces…</Typography>
            </Stack>
          </Box>
        ) : null}

        {reviewView === 'traces' && activeFootageWindow?.upload.videoUrl && activeFootageReplayWindow ? (
          <Box sx={{ gridArea: 'video', minWidth: 0, overflow: 'hidden' }}>
            <GoProSyncedVideoOverlay
              key={`inline-${activeFootageWindow.upload.uploadSessionId}`}
              layout="inline"
              videoSrc={activeFootageWindow.upload.videoUrl}
              replay={replay}
              clipStartTimecodeMs={activeFootageReplayWindow.startTimecodeMs}
              videoStartTimecodeMs={activeFootageWindow.startTimecodeMs}
              title={activeFootageWindow.upload.originalName}
              onVideoDuration={(durationSec) => handleFootageMediaDuration(activeFootageWindow.upload.uploadSessionId, durationSec)}
            />
          </Box>
        ) : null}

        {reviewView === 'traces' && activeTimelineLayer && activeTimelineWindows.length > 0 ? (
          <Box
            sx={{
              gridArea: 'storyboard',
              minWidth: 0,
              overflow: 'hidden',
              border: `1px solid ${isDark ? 'rgba(148,163,184,0.18)' : '#E5E7EB'}`,
              borderRadius: 1.5,
              bgcolor: traceChrome.panelBg,
            }}
          >
            <SessionTraceTimelineLive
              replay={replay}
              layer={activeTimelineLayer}
              windows={activeTimelineWindows}
              sourceLabel={activeTimelineLabel}
              alignmentMapper={timelineAlignmentMappers.get(activeTimelineLayer.id)}
              zoomRange={traceZoomRange}
              onSelectLap={activeTimelineLapSelect}
              chrome={traceChrome}
            />
          </Box>
        ) : null}
        </Box>

        <KartSetupComparisonDrawer
          open={kartSetupsOpen && replayLayers.length > 0}
          onClose={() => setKartSetupsOpen(false)}
          layers={replayLayers}
          sessions={sessions}
          karts={karts}
          contexts={setupContexts}
          loading={setupContextsLoading}
          onSetupChange={readOnly ? undefined : handleSetupChange}
          onSetupAssignmentChange={readOnly ? undefined : handleSetupAssignmentChange}
          onCustomAttributesChange={readOnly ? undefined : handleCustomAttributesChange}
          onKartChange={readOnly ? undefined : handleKartChange}
        />

        {reviewView === 'map' && showTraceDrawer && primaryLayer ? (
          <Collapse in={traceDrawerOpen} timeout={180} unmountOnExit sx={{ flexShrink: 0 }}>
            <Box sx={traceDrawerShellSx(isDark)}>
              <Stack direction="row" sx={traceDrawerHeaderSx(isDark)}>
                <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', minWidth: 0 }}>
                  <ShowChartIcon
                    sx={{ fontSize: 18, color: traceChrome.iconAccent, flexShrink: 0 }}
                  />
                  <Typography
                    sx={{ fontSize: '0.9rem', fontWeight: 900, color: traceChrome.title }}
                  >
                    Lap traces
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.76rem', fontWeight: 750, color: traceChrome.subtitle }}
                  >
                    {primaryLayer.label} |{' '}
                    {replayLayers.length > 1 ? `${replayLayers.length} laps` : 'lap replay'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', flexShrink: 0 }}>
                  <Button
                    size="small"
                    variant={tracePickerOpen ? 'contained' : 'outlined'}
                    startIcon={<RouteIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setTracePickerOpen((open) => !open)}
                    sx={{
                      height: 30,
                      px: 1,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontSize: '0.72rem',
                      fontWeight: 850,
                      color:
                        tracePickerOpen && !isDark ? '#7C3AED' : isDark ? '#FFFFFF' : '#374151',
                      borderColor: traceChrome.tracesButtonBorder,
                      bgcolor: tracePickerOpen
                        ? traceChrome.tracesButtonActiveBg
                        : traceChrome.tracesButtonBg,
                      '&:hover': {
                        borderColor: traceChrome.tracesButtonHoverBorder,
                        bgcolor: tracePickerOpen
                          ? traceChrome.tracesButtonActiveBg
                          : traceChrome.tracesButtonHoverBg,
                      },
                    }}
                  >
                    Traces
                  </Button>
                  <IconButton
                    aria-label="More trace actions"
                    size="small"
                    onClick={(event) => setTraceActionsMenuAnchor(event.currentTarget)}
                    sx={{
                      width: 30,
                      height: 30,
                      color: isDark ? '#FFFFFF' : '#475569',
                      border: `1px solid ${traceChrome.tracesButtonBorder}`,
                      borderRadius: '6px',
                    }}
                  >
                    <MoreHorizIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    aria-label="Close lap traces"
                    size="small"
                    onClick={() => setTraceDrawerOpen(false)}
                    sx={{
                      width: 30,
                      height: 30,
                      color: traceChrome.closeIcon,
                      border: `1px solid ${traceChrome.closeBorder}`,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Stack>
              </Stack>
              <SessionCompareTracePanelLive
                replay={replay}
                layers={replayLayers}
                durationSec={replay.durationSec}
                alignmentMode={traceAlignmentMode}
                onTraceZoomRangeChange={setTraceZoomRange}
                tracePickerOpen={tracePickerOpen}
                onTracePickerClose={() => setTracePickerOpen(false)}
                saveDialogOpen={traceSaveDialogOpen}
                onSaveDialogClose={() => setTraceSaveDialogOpen(false)}
                onSeekSec={(sec) => {
                  replay.pause();
                  replay.seekElapsed(sec);
                }}
              />
            </Box>
          </Collapse>
        ) : null}

        <Menu
          anchorEl={traceActionsMenuAnchor}
          open={Boolean(traceActionsMenuAnchor)}
          onClose={() => setTraceActionsMenuAnchor(null)}
          MenuListProps={{ 'aria-label': 'Trace actions' }}
          slotProps={{ paper: { sx: { minWidth: 176, mt: 0.5 } } }}
        >
          {(['time', 'gps'] as const).map((mode) => (
            <MenuItem
              key={mode}
              role="menuitemradio"
              disabled={mode === 'gps' && (!primaryLayer || replayLayers.some((layer) => layer.points.length < 2))}
              aria-label={`Align traces by ${mode === 'gps' ? 'GPS' : 'time'}`}
              aria-checked={traceAlignmentMode === mode}
              selected={traceAlignmentMode === mode}
              onClick={() => {
                setTraceAlignmentMode(mode);
                window.localStorage.setItem(TRACE_ALIGNMENT_STORAGE_KEY, mode);
                setTraceActionsMenuAnchor(null);
              }}
            >
              Align by {mode === 'gps' ? 'GPS' : 'Time'}
            </MenuItem>
          ))}
          <MenuItem
            onClick={() => {
              setTraceActionsMenuAnchor(null);
              setTracePickerOpen(false);
              setTraceSaveDialogOpen(true);
            }}
            sx={{ fontSize: '0.76rem' }}
          >
            <ListItemIcon><SaveOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Save trace view" />
          </MenuItem>
          {replayLayers.length > 0 ? (
            <MenuItem
              onClick={() => {
                setTraceActionsMenuAnchor(null);
                setKartSetupsOpen(true);
              }}
              sx={{ fontSize: '0.76rem' }}
            >
              <ListItemIcon><TuneOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Kart setups" />
            </MenuItem>
          ) : null}
        </Menu>
      </Box>

      {hasTurnSpeedDrawer ? (
        <Drawer
          anchor="right"
          open={turnDrawerOpen}
          onClose={() => setTurnDrawerOpen(false)}
          ModalProps={{
            disableEnforceFocus: true,
            disableScrollLock: true,
          }}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: TURN_SPEED_DRAWER_WIDTH },
              maxWidth: '100vw',
              boxSizing: 'border-box',
              bgcolor: isDark ? '#010D18' : '#FFFFFF',
              borderLeft: isDark ? '1px solid rgba(36,62,82,0.88)' : '1px solid #E5E7EB',
              color: isDark ? '#FFFFFF' : '#111827',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.4,
              py: 0.9,
              borderBottom: isDark ? '1px solid rgba(36,62,82,0.72)' : '1px solid #E5E7EB',
              flexShrink: 0,
            }}
          >
            <Stack spacing={0.15} sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontSize: '0.9rem', fontWeight: 900, color: isDark ? '#FFFFFF' : '#111827' }}
              >
                Turn speeds
              </Typography>
              <Typography
                sx={{ fontSize: '0.72rem', fontWeight: 750, color: isDark ? '#64748B' : '#6B7280' }}
              >
                Entrance / Apex / Exit | {speedUnit}
              </Typography>
            </Stack>
            <IconButton
              aria-label="Close turn speeds"
              size="small"
              onClick={() => setTurnDrawerOpen(false)}
              sx={{
                width: 30,
                height: 30,
                color: isDark ? '#A6B0BE' : '#6B7280',
                border: isDark ? '1px solid rgba(36,62,82,0.72)' : '1px solid #E5E7EB',
              }}
            >
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Stack>

          <Box sx={{ overflow: 'auto', minHeight: 0, flex: 1 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  replayLayers.length > 1 ? '34px 74px minmax(0,1fr)' : '34px minmax(0,1fr)',
                gap: 0.6,
                px: 1.1,
                py: 0.65,
                borderBottom: isDark ? '1px solid rgba(36,62,82,0.78)' : '1px solid #E5E7EB',
                bgcolor: isDark ? 'rgba(2,16,30,0.9)' : '#F9FAFB',
              }}
            >
              <Typography
                sx={{ fontSize: '0.66rem', color: isDark ? '#94A3B8' : '#6B7280', fontWeight: 900 }}
              >
                T
              </Typography>
              <Typography
                sx={{ fontSize: '0.66rem', color: isDark ? '#C4B5FD' : '#7C3AED', fontWeight: 900 }}
              >
                {replayLayers[0]?.label ?? 'P'} E/A/X
              </Typography>
              {replayLayers.length > 1 ? (
                <Typography
                  sx={{
                    fontSize: '0.66rem',
                    color: isDark ? '#94A3B8' : '#6B7280',
                    fontWeight: 900,
                  }}
                >
                  Compare | ΔA
                </Typography>
              ) : null}
            </Box>

            {cornerSpeedRows.length > 0 ? (
              cornerSpeedRows.map((row) => (
                <Box
                  key={`corner-row-${row.turnLabel}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns:
                      replayLayers.length > 1 ? '34px 74px minmax(0,1fr)' : '34px minmax(0,1fr)',
                    gap: 0.6,
                    px: 1.1,
                    py: 0.75,
                    borderBottom: isDark ? '1px solid rgba(36,62,82,0.38)' : '1px solid #F3F4F6',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.76rem',
                      color: isDark ? '#E2E8F0' : '#374151',
                      fontWeight: 850,
                    }}
                  >
                    {row.turnLabel}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 740,
                      color: row.primaryCorner
                        ? isDark
                          ? '#D8B4FE'
                          : '#7C3AED'
                        : isDark
                          ? '#64748B'
                          : '#9CA3AF',
                    }}
                  >
                    {formatCornerTriplet(
                      row.primaryCorner?.entrySpeedKph ?? null,
                      row.primaryCorner?.apexSpeedKph ?? null,
                      row.primaryCorner?.exitSpeedKph ?? null,
                    )}
                  </Typography>
                  {replayLayers.length > 1 ? (
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      {row.compareCorners.map((compare) => (
                        <Box
                          key={`${row.turnLabel}-${compare.layerId}`}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '18px minmax(0,1fr) 38px',
                            gap: 0.35,
                            alignItems: 'center',
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: '0.66rem', color: compare.color, fontWeight: 900 }}
                          >
                            {compare.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.67rem',
                              color: compare.color,
                              fontWeight: 740,
                              minWidth: 0,
                            }}
                          >
                            {formatCornerTriplet(
                              compare.corner?.entrySpeedKph ?? null,
                              compare.corner?.apexSpeedKph ?? null,
                              compare.corner?.exitSpeedKph ?? null,
                            )}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.66rem',
                              fontWeight: 780,
                              textAlign: 'right',
                              color:
                                compare.apexDeltaKph === null
                                  ? isDark
                                    ? '#94A3B8'
                                    : '#9CA3AF'
                                  : compare.apexDeltaKph >= 0
                                    ? '#22C55E'
                                    : '#EF4444',
                            }}
                          >
                            {formatApexDelta(compare.apexDeltaKph)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : null}
                </Box>
              ))
            ) : (
              <Box sx={{ px: 1.2, py: 1.4 }}>
                <Typography
                  sx={{
                    fontSize: '0.76rem',
                    color: isDark ? '#94A3B8' : '#6B7280',
                    fontWeight: 700,
                  }}
                >
                  No turn speeds available.
                </Typography>
              </Box>
            )}
          </Box>
        </Drawer>
      ) : null}
    </>
  );
}
